import React, { useState, useEffect, useRef, useMemo } from "react";
import { useAppNavigate } from "utils/router";
import { Eye, EyeOff, Info, BookOpen, CalendarIcon, X, ChevronDown, CheckCircle, Check, Zap, CreditCard, UserPlus, Database, Loader2, AlertTriangle, Plug, Copy, ExternalLink, Lock, Pencil } from "lucide-react";
import { FormSection } from "./components/FormSection";
import { GroupIdDecisionTree } from "./components/GroupIdDecisionTree";
import { GroupIdMappingModal } from "./components/GroupIdMappingModal";
import { RevenueVisualization } from "./components/RevenueVisualization";
import { SetupGuide } from "./components/SetupGuide";
import { TestSyncResults } from "./components/TestSyncResults";
import { InviteTeamModal } from "../../Common/InviteTeamModal";
import { useGetEnvironmentsForProductLazyQuery, useCreateEnvironmentCaMutation, EnvironmentType } from "store/graphql/codegen/auto-gen/configurator";
import { configuratorClient, appPricingClient, revenueClient } from "store/graphql/apollo/clients";
import { TeamId } from "store/redux/reducers/team/teamConstants";
import { SiStripe } from 'react-icons/si';
import fileLogo from '../../../../assets/images/logo/file.svg';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faLastfmSquare, faStripe } from '@fortawesome/free-brands-svg-icons'
import { useTestStripeConnectionLazyQuery, useCreateAirbyteIntegrationMutation, StripeEnvironmentType, useGetSyncStatusLazyQuery, DataSource, useGetStripeAccountDetailsLazyQuery, useUpdateAirbyteSourceMutation, useTriggerSyncMutation, useResetAndResyncAirbyteDataMutation, IntegrationType } from '../../../../store/graphql/codegen/auto-gen/revenue-report'
import { useDemoMode } from "../../../../context/DemoModeContext";
import { getProductId, getUser, getProductName, getEnvironmentDisplayName, getStripeFormData, setStripeFormData, removeStripeFormData } from "utils/localStorage";
import {
    simulateTestStripeConnection,
    simulateCreateStripeIntegration,
    DEMO_ENVIRONMENT,
    DEMO_TEST_SCENARIOS,
    type TestStripeConnectionResponse
} from './components/stripeDemoHelpers';
import { getStripeEntityImpact, sortMissingEntitiesForDisplay } from './utils/stripeEntityImpact';
import { validateEmail } from '../utils/utils'
import { EmailManager } from "../RevenueAnalytics";
import { useLocation, useSearchParams } from "react-router-dom";
import { DemoStripeModal } from './components/DemoStripeModal'
import { FixInstructionsModal, FixInstructionsContent } from './components/FixInstructionsModal'

interface StripeIntegrationProps {
    sourceConfigurations: Record<string, any>;
    updateSourceConfiguration: (sourceId: string, config: any) => void;
    onComplete?: (config: any) => void;
    loadedStripeData?: any;
    selectedSources: string;
    config: Record<string, any>;
    setConfig: (config: any) => void;
    onStepComplete: (value: boolean, overrideValues?: any) => void;
    emailManager: EmailManager;
    onAutoSave: () => void;
    cleanOAuthParams: () => void;
    openSetupGuideRef?: React.MutableRefObject<(() => void) | null>;
    onOpenIntegrationsList: () => void;
    onTrackCompletion?: () => void;
}

interface FormData {
    sourceName: string;
    accountId: string | undefined;
    accountIdEntryMode?: 'oauth' | 'manual';
    stripeAccountName?: string;
    secretKey: string;
    dataRetentionLocation: string;
    syncStartDate: Date;
    groupIdMapping: boolean;
    environmentId: string;
    environmentName: string;
    stripeEnvironment: string;
}

interface StepConfig {
    id: string;
    number: number;
    title: string;
    description: string;
    subtitle?: string;
    isCompleted: boolean;
    isActive: boolean;
    required: boolean;
}

interface NewEnvironmentForm {
    displayName: string;
    environmentType: EnvironmentType;
}

interface TestingStep {
    id: string;
    title: string;
    loadingText: string;
    successText: string;
    errorText: string;
    status: 'pending' | 'loading' | 'success' | 'error';
    missingEntities?: string[];
    selectedIdentifierKey?: string;
    customerMetaDataKeys?: string[];
}

type SyncStatusEntry = {
    dataSource?: string | null;
    status?: string | null;
    syncType?: string | null;
    lastUpdatedAt?: string | null;
    startTime?: string | null;
};

function pickStripeDataSyncStatus(entries: SyncStatusEntry[] | null | undefined) {
    if (!entries?.length) return null;
    const stripe = entries.filter((e) => (e.dataSource || '').toString().toUpperCase() === 'STRIPE');
    if (stripe.length === 0) return null;

    const statusRank = (s: string | null | undefined) => {
        const u = (s || '').toLowerCase();
        if (u === 'running' || u === 'pending') return 0;
        if (u === 'failed') return 1;
        if (u === 'succeeded') return 2;
        if (u === 'cancelled') return 3;
        return 4;
    };
    const recency = (e: SyncStatusEntry) => {
        const st = e.startTime ? new Date(e.startTime).getTime() : 0;
        const lu = e.lastUpdatedAt ? new Date(e.lastUpdatedAt).getTime() : 0;
        return Math.max(st, lu);
    };

    return [...stripe].sort((a, b) => {
        const ra = statusRank(a.status);
        const rb = statusRank(b.status);
        if (ra !== rb) return ra - rb;
        return recency(b) - recency(a);
    })[0];
}

const getDefaultSyncDate = () => {
    const date = new Date();
    date.setMonth(date.getMonth() - 12);
    return date;
};

const STRIPE_TABLES = {
    available: [
        "customers", "subscriptions", "subscription_items", "subscription_schedules", "subscription_schedule_phases", "invoices", "invoice_items", "invoice_line_items",
        "credit_notes", "payments", "payment_intents", "setup_intents", "setup_attempts", "charges", "refunds", "balance_transactions", "transfers", "payouts", "application_fees",
        "application_fee_refunds", "disputes", "coupon", "promotion_codes", "tax_rates", "usage_records", "usage_record_summaries", "mandates", "payment_methods",
        "payment_method_details", "sources", "source_metadata", "events", "files", "file_links", "customer_tax_ids", "customer_balance_transactions", "discounts", "account",
        "account_links", "capabilities", "external_accounts", "persons", "topups", "transfer_reversals", "country_specs", "reviews", "value_lists", "value_list_items",
        "issuing_authorizations", "issuing_cardholders", "issuing_disputes", "issuing_transactions", "locations"
    ],
    willSync: [
        "customers",
        "invoices",
        "invoice_items",
        "invoice_line_items",
        "plans",
        "prices",
        "products",
        "subscriptions",
        "subscriptions_items",
        "balance_transactions",
        "transactions",
        "charges",
        "disputes",
        "refunds",
        "payment_intent"
    ]
};

const curlCommands = {
    createCustomer: `curl https://api.stripe.com/v1/customers \
                        -u "sk_test_tR3PYbcVNZZ796tH88S4VQ2u:" \
                        -d name="Jenny Rosen" \
                        --data-urlencode email="jennyrosen@example.com"`,
    updateCustomer: `curl https://api.stripe.com/v1/customers/cus_NffrFeUfNV2Hib \
                        -u "sk_test_tR3PYbcVNZZ796tH88S4VQ2u:" \
                        -d "metadata[order_id]"=6735`
}

const normalizeKey = (key: string): string => {
  return key.toLowerCase().replace(/[_\s-]/g, '');
};

const findGroupIdKey = (keys: string[]): string | null => {
  const normalizedGroupId = normalizeKey('group_id');
  return keys.find(key => normalizeKey(key) === normalizedGroupId) || null;
};

const resolveSelectedAccountKey = (keys: string[], currentSelection?: string): string => {
    if (!keys.length) return currentSelection ?? 'group_id';
    const groupIdKey = findGroupIdKey(keys);
    if (groupIdKey) return groupIdKey;
    if (currentSelection && keys.includes(currentSelection)) return currentSelection;
    return keys[0];
};

type DemoSchemaScenario = 'group_id_found' | 'no_group_id' | 'no_metadata_keys';

const DEMO_SCHEMA_SCENARIO_OPTIONS: Array<{ id: DemoSchemaScenario; label: string }> = [
    { id: 'group_id_found', label: 'Has group_id' },
    { id: 'no_group_id', label: 'No group_id' },
    { id: 'no_metadata_keys', label: 'No metadata keys' }
];

const DEMO_SCHEMA_SCENARIO_METADATA_KEYS: Record<DemoSchemaScenario, string[]> = {
    group_id_found: ['group_id', 'organization_id', 'workspace_id'],
    no_group_id: ['organization_id', 'workspace_id', 'tenant_id'],
    no_metadata_keys: []
};

const DEFAULT_ACCOUNT_IDENTIFIER_KEY = 'group_id';

export const StripeIntegration: React.FC<StripeIntegrationProps> = ({
    sourceConfigurations,
    updateSourceConfiguration,
    onComplete,
    loadedStripeData,
    selectedSources,
    config,
    setConfig,
    onStepComplete,
    emailManager,
    onAutoSave,
    cleanOAuthParams,
    openSetupGuideRef,
    onOpenIntegrationsList,
    onTrackCompletion,
}) => {

    const [formData, setFormData] = useState<FormData>({
        sourceName: "Stripe",
        accountId: "",
        accountIdEntryMode: undefined,
        secretKey: "",
        dataRetentionLocation: "",
        syncStartDate: getDefaultSyncDate(),
        groupIdMapping: false,
        environmentId: "",
        environmentName: "",
        stripeEnvironment: "",
    });
    const { isDemoMode } = useDemoMode()
    const [showSecretKey, setShowSecretKey] = useState(false);
    const [isSetupGuideOpen, setIsSetupGuideOpen] = useState(false);
    const [showCalendar, setShowCalendar] = useState(false);
    const [selectedOption, setSelectedOption] = useState<"proceed" | "pause" | null>(null);
    const [showMappingModal, setShowMappingModal] = useState(false);
    const [errors, setErrors] = useState<Partial<FormData>>({});
    const [activeStep, setActiveStep] = useState<string>('environment');
    const [isManualStepChange, setIsManualStepChange] = useState(false);
    const [showEnvironmentDropdown, setShowEnvironmentDropdown] = useState(false);
    const [showStripeEnvironmentDropdown, setShowStripeEnvironmentDropdown] = useState(false);
    const [showNewEnvironmentForm, setShowNewEnvironmentForm] = useState(false);
    const [newEnvironmentForm, setNewEnvironmentForm] = useState<NewEnvironmentForm>({
        displayName: '',
        environmentType: EnvironmentType.NonProduction
    });
    const [isTesting, setIsTesting] = useState(false);
    const setupGuideStep3Ref = useRef<HTMLDivElement>(null);
    const setupGuideAccountIdRef = useRef<HTMLDivElement>(null);
    const [selectedAccountKey, setSelectedAccountKey] = useState<string>('group_id');
    const [showAccountKeyDropdown, setShowAccountKeyDropdown] = useState(false);
    const [customerMetaDataKeys, setCustomerMetaDataKeys] = useState<string[]>([]);
    const [groupIdFound, setGroupIdFound] = useState<boolean>(false);
    const [demoSchemaScenario, setDemoSchemaScenario] = useState<DemoSchemaScenario>('group_id_found');
    const [noGroupIdWarningDismissed, setNoGroupIdWarningDismissed] = useState(false);
    const [hasNoCustomerData, setHasNoCustomerData] = useState(false);
    const [missingEntitiesAcknowledged, setMissingEntitiesAcknowledged] = useState(false);
    const [isEntityErrorExpanded, setIsEntityErrorExpanded] = useState(true);
    const [testingSteps, setTestingSteps] = useState<TestingStep[]>([
        {
            id: 'connection',
            title: 'Connection',
            loadingText: 'Establishing Connection',
            successText: 'Connected',
            errorText: 'Connection Error',
            status: 'pending'
        },
        {
            id: 'data',
            title: 'Data Retrieval',
            loadingText: 'Retrieving Data Tables',
            successText: 'Data Retrieved',
            errorText: 'Unable to Retrieve Data',
            status: 'pending'
        },
        {
            id: 'schema',
            title: 'Schema Verification',
            loadingText: 'Verifying Schema',
            successText: 'Schema Verified',
            errorText: 'Schema Error Detected',
            status: 'pending'
        }
    ]);
    const ingestionLoadingStepsDefault = [
        {
            id: 'create-source',
            title: 'Creating Source',
            loadingText: 'Creating Source...',
            successText: 'Source Created',
            errorText: 'Failed to Create Source',
            status: 'pending'
        },
        {
            id: 'make-connection',
            title: 'Making Connection',
            loadingText: 'Making Connection...',
            successText: 'Connection Established',
            errorText: 'Connection Error',
            status: 'pending'
        },
        {
            id: 'sync-data',
            title: 'Syncing Data',
            loadingText: 'Starting Data Sync...',
            successText: 'Data Sync Started',
            errorText: 'Sync Error',
            status: 'pending'
        }
    ];

    const [testCompleted, setTestCompleted] = useState<boolean>(false);
    const [dotCount, setDotCount] = useState(1);
    const [isTablesExpanded, setIsTablesExpanded] = useState(false);

    const navigate = useAppNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();

    const [selectedTeam, setSelectedTeam] = useState<TeamId>();
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [isStep3Incomplete, setIsStep3Incomplete] = useState<boolean>(false);
    const [showFixInstructionsModal, setShowFixInstructionsModal] = useState(false);

    const [ingestionResult, setIngestionResult] = useState<{
        sourceId: string;
        connectionId: string;
        syncStartedAt: string;
        syncStatus: string;
        jobId: number;
    } | null>(null);
    const [ingestionError, setIngestionError] = useState<string | null>(null);

    const step1NumberRef = useRef<HTMLDivElement>(null);
    const step2NumberRef = useRef<HTMLDivElement>(null);
    const step3NumberRef = useRef<HTMLDivElement>(null);
    const step4NumberRef = useRef<HTMLDivElement>(null);

    const step2ContainerRef = useRef<HTMLDivElement>(null);
    const secretKeySectionRef = useRef<HTMLDivElement>(null);
    const step3ContainerRef = useRef<HTMLDivElement>(null);
    const step4ContainerRef = useRef<HTMLDivElement>(null);

    const substep2_2Ref = useRef<HTMLDivElement>(null);
    const substepFromUrl = useRef<number | null>(null);
    const userChosenStepRef = useRef<string | null>(null);
    const stripeConfigRef = useRef<Record<string, unknown> | null>(null);
    const didRestoreFromSavedConfigRef = useRef<string | boolean>(false);

    const localRef = useRef<boolean>(false)

    const [step3Completed, setStep3Completed] = useState<boolean>(
        !!loadedStripeData?.step3Completed || !!sourceConfigurations.stripe?.step3Completed
    );
    const [step4Completed, setStep4Completed] = useState<boolean>(
        !!loadedStripeData?.step4Completed || !!sourceConfigurations.stripe?.step4Completed
    );
    const [step4WasEverCompleted, setStep4WasEverCompleted] = useState<boolean>(
        !!loadedStripeData?.step4WasEverCompleted || 
        !!sourceConfigurations.stripe?.step4WasEverCompleted ||
        !!loadedStripeData?.step4CompletedAt ||
        !!sourceConfigurations.stripe?.step4CompletedAt
    );
    const [showIngestionLoadingSteps, setShowIngestionLoadingSteps] = useState(false);
    const [ingestionLoadingSteps, setIngestionLoadingSteps] = useState(ingestionLoadingStepsDefault);
    const [ingestionStepIndex, setIngestionStepIndex] = useState(0);
    const [expanded, setExpanded] = useState(false);

    const [syncStatus, setSyncStatus] = useState<'pending' | 'running' | 'succeeded' | 'failed'>('pending');
    const [isSyncPolling, setIsSyncPolling] = useState(false);
    const [lastSyncedDate, setLastSyncedDate] = useState<string | null>(null);
    const [accountIdLoading, setAccountIdLoading] = useState<boolean>(false)
    const [showDemoModal, setShowDemoModal] = useState(false);

    const [isEditingSourceName, setIsEditingSourceName] = useState(false);
    const [isEditingSecretKey, setIsEditingSecretKey] = useState(false);
    const [isEditingSyncDate, setIsEditingSyncDate] = useState(false);
    const [basicConfigSavedThisSession, setBasicConfigSavedThisSession] = useState(false);
    
    const hasSuccessfulIngestion = !!(
        sourceConfigurations.stripe?.ingestionResult?.sourceId && 
        sourceConfigurations.stripe?.ingestionResult?.connectionId
    );
    const isBasicConfigFieldsLocked = hasSuccessfulIngestion;
    
    const isBasicConfigStepCompleted = !!sourceConfigurations.stripe?.step2Completed || basicConfigSavedThisSession;
    const [originalSourceName, setOriginalSourceName] = useState('');
    const [originalSecretKey, setOriginalSecretKey] = useState('');
    const [originalSyncDate, setOriginalSyncDate] = useState<Date | null>(null);
    const [isSetupIncomplete, setIsSetupIncomplete] = useState(false);

    type PostSaveFlowMode = 'none' | 'apiKeyOnly' | 'syncDateOnly' | 'both';
    const [postSaveFlowMode, setPostSaveFlowMode] = useState<PostSaveFlowMode>('none');
    const [pendingSecretKeyChange, setPendingSecretKeyChange] = useState(
        !!(sourceConfigurations.stripe as any)?.pendingSecretKeyChange || 
        !!(loadedStripeData as any)?.pendingSecretKeyChange
    );
    const [pendingSyncDateChange, setPendingSyncDateChange] = useState(
        !!(sourceConfigurations.stripe as any)?.pendingSyncDateChange || 
        !!(loadedStripeData as any)?.pendingSyncDateChange
    );
    const [resyncStarted, setResyncStarted] = useState(false);
    const [requiresStep3CompletionForResync, setRequiresStep3CompletionForResync] = useState(false);
    const [updateSourceError, setUpdateSourceError] = useState<string | null>(null);
    const [isUpdatingSource, setIsUpdatingSource] = useState(false);

    const [getEnvironmentsForProduct, { data: environmentsData, loading: environmentsLoading }] = useGetEnvironmentsForProductLazyQuery({
        client: configuratorClient,
        fetchPolicy: "no-cache"
    });

    const [createEnvironmentCaMutation, { loading: createEnvironmentLoading }] = useCreateEnvironmentCaMutation({
        client: configuratorClient,
        fetchPolicy: "no-cache"
    });

    const [TestStripeConnection] = useTestStripeConnectionLazyQuery({
        client: appPricingClient,
        fetchPolicy: "no-cache"
    });

    const [GetStripeAccountDetails] = useGetStripeAccountDetailsLazyQuery({
        client: appPricingClient,
        fetchPolicy: "no-cache"
    });

    const [createAirbyteIntegration, { loading: createIntegrationLoading }] = useCreateAirbyteIntegrationMutation({
        client: revenueClient,
        fetchPolicy: "no-cache"
    });

    const [updateAirbyteSource, { loading: updateAirbyteSourceLoading }] = useUpdateAirbyteSourceMutation({
        client: revenueClient,
        fetchPolicy: "no-cache"
    });

    const [triggerSync, { loading: triggerSyncLoading }] = useTriggerSyncMutation({
        client: revenueClient,
        fetchPolicy: "no-cache"
    });

    const [resetAndResyncAirbyteData, { loading: resetAndResyncLoading }] = useResetAndResyncAirbyteDataMutation({
        client: revenueClient,
        fetchPolicy: "no-cache"
    });

    const [GetSyncStatus] = useGetSyncStatusLazyQuery({
        client: revenueClient,
        fetchPolicy: "no-cache"
    });

    const cleanupDeepLinkParams = () => {
        const params = new URLSearchParams(searchParams.toString());
        let changed = false;

        if (params.has('substep')) {
            params.delete('substep');
            changed = true;
        }
        if (params.has('openSetupGuide')) {
            params.delete('openSetupGuide');
            changed = true;
        }

        if (changed) {
            setSearchParams(params, { replace: true });
        }
    };

    const openSetupGuideAtStep3 = () => {
        setIsSetupGuideOpen(true);
        setTimeout(() => {
            if (setupGuideStep3Ref.current) {
                setupGuideStep3Ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }, 100);
    };

    const openSetupGuideAtAccountId = () => {
        setIsSetupGuideOpen(true);
        setTimeout(() => {
            if (setupGuideAccountIdRef.current) {
                setupGuideAccountIdRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }, 100);
    };

    const fetchEnvironments = async () => {
        try {
            await getEnvironmentsForProduct({
                variables: {
                    input: {
                        productId: getProductId() as string
                    }
                }
            });
        } catch (error) {
            console.error('Failed to fetch environments:', error);
        }
    };

    const handleDemoStripeConnect = () => {
        const demoAccountId = `acct_demo_${Math.random().toString(36).substring(2, 15)}`;

        setFormData(prev => ({
            ...prev,
            accountId: demoAccountId
        }));

        setShowDemoModal(false);
    };


    const areAllStepsSuccessful = () => {
        return testingSteps.every(step => step.status === 'success');
    };

    const useCopyToClipboard = (timeout = 2000) => {
        const [copiedStates, setCopiedStates] = useState<Record<string, boolean>>({});

        const copy = async (text: string, id: string) => {
            await navigator.clipboard.writeText(text);
            setCopiedStates(prev => ({ ...prev, [id]: true }));

            setTimeout(() => {
                setCopiedStates(prev => ({ ...prev, [id]: false }));
            }, timeout);
        };

        return { copiedStates, copy };
    };

    const handleSaveAndProceed = async () => {
        
        setStep3Completed(true);
        setIsStep3Incomplete(false);
        
        setBasicConfigSavedThisSession(true);

        saveConfiguration(false);

        userChosenStepRef.current = 'pause-fix';
        setActiveStep('pause-fix');
        onAutoSave()
        setTimeout(() => {
            step4NumberRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
    };

    const handleInviteAndProceedWithMetadata = async () => {
        
        setIsStep3Incomplete(true);
        setStep3Completed(true);
        setFormData(prev => ({ ...prev, groupIdMapping: true }));
        
        setBasicConfigSavedThisSession(true);

        saveConfiguration(true, {
            selectedIdentifierKey: DEFAULT_ACCOUNT_IDENTIFIER_KEY,
            groupIdMapping: true
        });

        setSelectedTeam(TeamId.REVOPS_TEAMS);
        setShowInviteModal(true);

        userChosenStepRef.current = 'pause-fix';
        setActiveStep('pause-fix');
        onAutoSave()
        setTimeout(() => {
            step4NumberRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
    };

    const handleNavigateToSecretKey = () => {
        userChosenStepRef.current = 'basic-config';
        setActiveStep('basic-config');
        setTimeout(() => {
            secretKeySectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
    };

    const handleProceedDespiteMissingEntities = () => {
        setMissingEntitiesAcknowledged(true);
        setIsEntityErrorExpanded(false);
        setIsStep3Incomplete(true);
        saveConfiguration(true, { missingEntitiesAcknowledged: true });
        onAutoSave();
    };

    const handleProceedAnywayWithoutInvite = async () => {
        
        setIsStep3Incomplete(true);
        setStep3Completed(true);
        setFormData(prev => ({ ...prev, groupIdMapping: true }));
        
        setBasicConfigSavedThisSession(true);

        saveConfiguration(true, {
            selectedIdentifierKey: DEFAULT_ACCOUNT_IDENTIFIER_KEY,
            groupIdMapping: true
        });

        userChosenStepRef.current = 'pause-fix';
        setActiveStep('pause-fix');
        onAutoSave()
        setTimeout(() => {
            step4NumberRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
    };


    const { copiedStates, copy } = useCopyToClipboard()

    const testIntegration = async (scenario: keyof typeof DEMO_TEST_SCENARIOS = 'SUCCESS') => {
        if (isDemoMode) {
            const result = await simulateTestStripeConnection(scenario);
            const metadataKeys = result.customerMetaDataKeys ?? [];
            const resolvedKey = resolveSelectedAccountKey(metadataKeys, selectedAccountKey);
            const hasGroupIdKey = !!findGroupIdKey(metadataKeys);
            setCustomerMetaDataKeys(metadataKeys);
            setGroupIdFound(hasGroupIdKey);
            setNoGroupIdWarningDismissed(hasGroupIdKey);
            if (metadataKeys.length > 0) setSelectedAccountKey(resolvedKey);

            return result;
        }

        try {
            const result = await TestStripeConnection({
                variables: {
                    input: {
                        environmentId: formData.environmentId,
                        stripeKey: formData.secretKey
                    }
                }
            });

            const response = result.data?.testStripeConnection;
            const metadataKeys = response?.customerMetaDataKeys ?? [];
            const resolvedKey = resolveSelectedAccountKey(metadataKeys, selectedAccountKey);
            const hasGroupIdKey = !!findGroupIdKey(metadataKeys);
            setCustomerMetaDataKeys(metadataKeys);
            if (metadataKeys.length > 0) setSelectedAccountKey(resolvedKey);
            setGroupIdFound(hasGroupIdKey);
            setNoGroupIdWarningDismissed(hasGroupIdKey);

            return response;
        } catch (error) {
            console.error('Test connection error:', error);
            throw error;
        }
    };

    const effectiveFlowMode = useMemo(() => {
        if (pendingSecretKeyChange || pendingSyncDateChange) {
            return pendingSecretKeyChange && pendingSyncDateChange
                ? 'both'
                : pendingSyncDateChange
                  ? 'syncDateOnly'
                  : 'apiKeyOnly';
        }
        return postSaveFlowMode;
    }, [pendingSecretKeyChange, pendingSyncDateChange, postSaveFlowMode]);

    const stepsConfig: StepConfig[] = [
        {
            id: 'environment',
            number: 1,
            title: 'Map Environments',
            description: 'Select the Stripe environment that will map with current App environment and will be used when establishing an connection.',
            subtitle: 'Select the Stripe environment to connect with Current App environment',
            isCompleted: !!(formData.environmentId && formData.stripeEnvironment),
            isActive: activeStep === 'environment',
            required: true
        },
        {
            id: 'basic-config',
            number: 2,
            title: 'Basic Configuration',
            description: 'Configure your Stripe connection details',
            isCompleted: !!(formData.sourceName && formData.accountId && formData.secretKey && formData.syncStartDate),
            isActive: activeStep === 'basic-config',
            required: true
        },
        {
            id: 'revenue-correlation',
            number: 3,
            title: 'Test Your Stripe Connection',
            description: 'Test your Stripe credentials before starting Data Ingestion',
            isCompleted: step3Completed || areAllStepsSuccessful(),
            isActive: activeStep === 'revenue-correlation',
            required: true
        },
        {
            id: 'pause-fix',
            number: 4,
            title:
                (effectiveFlowMode === 'apiKeyOnly' ||
                effectiveFlowMode === 'both' ||
                effectiveFlowMode === 'syncDateOnly') &&
                step4WasEverCompleted
                    ? 'Resync Stripe data'
                    : 'Start Ingestion',
            description:
                (effectiveFlowMode === 'apiKeyOnly' ||
                effectiveFlowMode === 'both' ||
                effectiveFlowMode === 'syncDateOnly') &&
                step4WasEverCompleted
                    ? 'Start a new sync with modified configurations'
                    : 'Finalize your Stripe integration',
            isCompleted: step4Completed || false,
            isActive: activeStep === 'pause-fix',
            required: true
        }
    ];

    const handleUpdateNotificationEmails = () => {
        const updatedConfig = {
            notificationEmails: {
                userEmail: getUser()?.email,
                additionalEmails: emailManager.state.emails
            }
        };

        updateSourceConfiguration('stripe', updatedConfig);
        setConfig((prev: any) => ({
            ...prev,
            ...updatedConfig
        }));
        emailManager.resetUpdateButton();
    };

    useEffect(() => {
        if (openSetupGuideRef) {
            openSetupGuideRef.current = () => setIsSetupGuideOpen(true);
        }
        return () => {
            if (openSetupGuideRef) {
                openSetupGuideRef.current = null;
            }
        };
    }, [openSetupGuideRef]);

    stripeConfigRef.current = sourceConfigurations.stripe ?? null;

    useEffect(() => {
        const saved = sourceConfigurations?.stripe;
        if (!saved || Object.keys(saved).length === 0) return;

        const savedDataKey = JSON.stringify({
            accountId: saved.accountId,
            accountIdEntryMode: saved.accountIdEntryMode,
            secretKey: saved.secretKey?.substring(0, 10),
            syncStartDate: saved.syncStartDate,
            step2Completed: saved.step2Completed,
            step3Completed: saved.step3Completed,
            step4Completed: saved.step4Completed,
            ingestionResult: saved.ingestionResult?.sourceId
        });

        if (didRestoreFromSavedConfigRef.current === savedDataKey) return;
        didRestoreFromSavedConfigRef.current = savedDataKey;

        setFormData(prev => ({
            ...prev,
            sourceName: saved.sourceName ?? prev.sourceName,
            accountId: saved.accountId ?? prev.accountId,
            accountIdEntryMode: saved.accountIdEntryMode ?? prev.accountIdEntryMode,
            stripeAccountName: saved.stripeAccountName ?? prev.stripeAccountName,
            secretKey: saved.secretKey ?? prev.secretKey,
            dataRetentionLocation: saved.dataRetentionLocation ?? prev.dataRetentionLocation,
            syncStartDate: saved.syncStartDate ? new Date(saved.syncStartDate) : prev.syncStartDate,
            groupIdMapping: saved.groupIdMapping ?? prev.groupIdMapping,
            environmentId: saved.environmentId ?? prev.environmentId,
            environmentName: saved.environmentName ?? prev.environmentName,
            stripeEnvironment: saved.stripeEnvironment ?? prev.stripeEnvironment
        }));

        setIsEditingSourceName(false);
        setIsEditingSecretKey(false);
        setIsEditingSyncDate(false);
        setOriginalSourceName('');
        setOriginalSecretKey('');
        setOriginalSyncDate(null);

        if (Array.isArray(saved.testingSteps)) {
            setTestingSteps(saved.testingSteps);
        }
        if (Array.isArray(saved.customerMetaDataKeys)) {
            setCustomerMetaDataKeys(saved.customerMetaDataKeys);
        }
        if (typeof saved.groupIdFound === 'boolean') {
            setGroupIdFound(saved.groupIdFound);
        }
        if (typeof saved.testCompleted === 'boolean') {
            setTestCompleted(saved.testCompleted);
        }
        if (typeof saved.isStep3Incomplete === 'boolean') {
            setIsStep3Incomplete(saved.isStep3Incomplete);
        }
        if (typeof (saved as { missingEntitiesAcknowledged?: boolean }).missingEntitiesAcknowledged === 'boolean') {
            const acknowledged = (saved as { missingEntitiesAcknowledged: boolean }).missingEntitiesAcknowledged;
            setMissingEntitiesAcknowledged(acknowledged);
            setIsEntityErrorExpanded(!acknowledged);
        }
        if (typeof saved.step3Completed === 'boolean') {
            setStep3Completed(saved.step3Completed);
        }
        if (typeof saved.step4Completed === 'boolean') {
            setStep4Completed(prev => {
                if (saved.step4Completed === true) return true;
                if (
                    saved.step4Completed === false &&
                    postSaveFlowMode === 'apiKeyOnly' &&
                    resyncStarted &&
                    prev
                ) {
                    return true;
                }
                return saved.step4Completed;
            });
        }
        if (saved.ingestionResult) {
            setIngestionResult(saved.ingestionResult);
            const statusToUse = saved.ingestionResult.syncStatus || (saved as any).syncStatus;
            if (statusToUse) {
                setSyncStatus(statusToUse as any);
                if (statusToUse === 'running' || statusToUse === 'pending') {
                    setIsSyncPolling(true);
                }
            }
        } else if ((saved as any).syncStatus) {
            setSyncStatus((saved as any).syncStatus as any);
            if ((saved as any).syncStatus === 'running' || (saved as any).syncStatus === 'pending') {
                setIsSyncPolling(true);
            }
        }

        if (typeof saved.isSetupIncomplete === 'boolean') {
            setIsSetupIncomplete(saved.isSetupIncomplete);
        }

        // Load flags from config - only set to true, never back to false
        // (flags are only set to false on cancel or after successful resync)
        if ((saved as any).pendingSecretKeyChange === true) {
            setPendingSecretKeyChange(true);
        }

        if ((saved as any).pendingSyncDateChange === true) {
            setPendingSyncDateChange(true);
        }

    
        if ((saved as any).pendingSecretKeyChange || (saved as any).pendingSyncDateChange) {
            const mode: PostSaveFlowMode =
                (saved as any).pendingSecretKeyChange && (saved as any).pendingSyncDateChange
                    ? 'both'
                    : (saved as any).pendingSyncDateChange
                      ? 'syncDateOnly'
                      : (saved as any).pendingSecretKeyChange
                        ? 'apiKeyOnly'
                        : 'none';
            setPostSaveFlowMode(mode);
        }

        if (!isManualStepChange) {
            if (saved.step4Completed) {
                setActiveStep('pause-fix');
            } else if (saved.step3Completed) {
                setActiveStep('revenue-correlation');
            } else if (saved.isSetupIncomplete) {
                setActiveStep('basic-config');
            } else if (saved.step2Completed) {
                setActiveStep('revenue-correlation');
            } else if (saved.step1Completed) {
                setActiveStep('basic-config');
            }
        }
    }, [sourceConfigurations, isManualStepChange, postSaveFlowMode, resyncStarted]);

    useEffect(() => {
        const isResyncFlow =
            (effectiveFlowMode === 'apiKeyOnly' ||
            effectiveFlowMode === 'both' ||
            effectiveFlowMode === 'syncDateOnly') &&
            sourceConfigurations.stripe?.step4Completed;

        const shouldLoadStatus =
            activeStep === 'pause-fix' &&
            !!formData.environmentId &&
            !!ingestionResult &&
            (!isResyncFlow || resyncStarted);

        if (shouldLoadStatus) {
            const timeoutId = setTimeout(() => {
                const loadSyncStatus = async () => {
                    try {
                        const result = await GetSyncStatus({
                            variables: {
                                input: {
                                    environmentId: formData.environmentId,
                                    dataSource: [DataSource.Stripe]
                                }
                            }
                        });

                        const syncStatusData = pickStripeDataSyncStatus(
                            result.data?.getSyncStatus?.syncStatus as SyncStatusEntry[] | undefined
                        );
                        if (syncStatusData) {
                            const status = syncStatusData.status;
                            setSyncStatus(status as any);
                            const ts = syncStatusData.lastUpdatedAt || syncStatusData.startTime;
                            if (ts) {
                                setLastSyncedDate(ts);
                            } else if (ingestionResult.syncStartedAt && !lastSyncedDate) {
                                setLastSyncedDate(ingestionResult.syncStartedAt);
                            }
                            if (status === 'running' || status === 'pending') {
                                setIsSyncPolling(true);
                            }
                        }
                    } catch (error) {
                        console.error('Error loading sync status:', error);
                    }
                };

                loadSyncStatus();
            }, 100);

            return () => clearTimeout(timeoutId);
        }
    }, [activeStep, ingestionResult, formData.environmentId, GetSyncStatus, postSaveFlowMode, resyncStarted]);

    useEffect(() => {
        const isResyncFlow =
            (effectiveFlowMode === 'apiKeyOnly' ||
            effectiveFlowMode === 'both' ||
            effectiveFlowMode === 'syncDateOnly') &&
            sourceConfigurations.stripe?.step4Completed;
        if (isResyncFlow && !resyncStarted && !triggerSyncLoading && !resetAndResyncLoading) {
            setResyncStarted(false);
            setIsSyncPolling(false);
            setSyncStatus('pending');
            setLastSyncedDate(null);
        }
    }, [effectiveFlowMode, sourceConfigurations.stripe?.step4Completed, resyncStarted, triggerSyncLoading, resetAndResyncLoading]);

    useEffect(() => {
        setRequiresStep3CompletionForResync(
            (effectiveFlowMode === 'apiKeyOnly' ||
                effectiveFlowMode === 'both' ||
                effectiveFlowMode === 'syncDateOnly') &&
            sourceConfigurations.stripe?.step4Completed
        );
    }, [effectiveFlowMode, sourceConfigurations.stripe?.step4Completed]);

    useEffect(() => {
        if (!isSyncPolling || !formData.environmentId) return;

        const isResyncFlow =
            (postSaveFlowMode === 'apiKeyOnly' ||
            postSaveFlowMode === 'both' ||
            postSaveFlowMode === 'syncDateOnly') &&
            sourceConfigurations.stripe?.step4Completed;
        if (isResyncFlow && !resyncStarted) return;

        let pollInterval: NodeJS.Timeout;
        let maxAttempts = 40;
        let attempts = 0;

        const pollSyncStatus = async () => {
            try {
                attempts++;

                if (attempts > maxAttempts) {
                    setSyncStatus('failed');
                    setIsSyncPolling(false);
                    clearInterval(pollInterval);
                    return;
                }

                const result = await GetSyncStatus({
                    variables: {
                        input: {
                            environmentId: formData.environmentId,
                            dataSource: [DataSource.Stripe]
                        }
                    }
                });

                const syncStatusData = pickStripeDataSyncStatus(
                    result.data?.getSyncStatus?.syncStatus as SyncStatusEntry[] | undefined
                );
                if (syncStatusData) {
                    const status = syncStatusData.status;
                    setSyncStatus(status as any);
                    const ts = syncStatusData.lastUpdatedAt || syncStatusData.startTime;
                    if (ts) {
                        setLastSyncedDate(ts);
                    }

                    setIngestionResult(prev =>
                        prev ? { ...prev, syncStatus: status || prev.syncStatus } : null
                    );
                    const currentStripe = stripeConfigRef.current || {};
                    const currentIngestion = (currentStripe.ingestionResult as Record<string, unknown>) || {};
                    updateSourceConfiguration('stripe', {
                        ...currentStripe,
                        ingestionResult: { ...currentIngestion, syncStatus: status }
                    });

                    if (status === 'succeeded' || status === 'failed') {
                        setIsSyncPolling(false);
                        clearInterval(pollInterval);
                    }
                }
            } catch (error) {
                console.error('Error polling sync status:', error);
                setSyncStatus('failed');
                setIsSyncPolling(false);
                clearInterval(pollInterval);
            }
        };

        pollSyncStatus();
        pollInterval = setInterval(pollSyncStatus, 30000);

        return () => {
            clearInterval(pollInterval);
        };
    }, [isSyncPolling, formData.environmentId, GetSyncStatus, postSaveFlowMode, resyncStarted, updateSourceConfiguration]);

    useEffect(() => {
        if (!showIngestionLoadingSteps) return;

        if (ingestionError) {
            setIngestionLoadingSteps(prev =>
                prev.map(step => ({
                    ...step,
                    status: 'error'
                }))
            );
            return;
        }

        if (ingestionStepIndex < ingestionLoadingSteps.length - 1) {
            setIngestionLoadingSteps(prev =>
                prev.map((step, idx) =>
                    idx === ingestionStepIndex ? { ...step, status: 'loading' } : step
                )
            );

            const timer = setTimeout(() => {
                setIngestionLoadingSteps(prev =>
                    prev.map((step, idx) =>
                        idx === ingestionStepIndex ? { ...step, status: 'success' } : step
                    )
                );
                setIngestionStepIndex(i => i + 1);
            }, 3000);

            return () => clearTimeout(timer);
        }

        if (ingestionStepIndex === ingestionLoadingSteps.length - 1) {
            setIngestionLoadingSteps(prev =>
                prev.map((step, idx) =>
                    idx === ingestionStepIndex
                        ? { ...step, status: createIntegrationLoading ? 'loading' : 'success' }
                        : step
                )
            );
        }
    }, [showIngestionLoadingSteps, ingestionStepIndex, createIntegrationLoading, ingestionLoadingSteps.length, ingestionError]);

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        const stateParam = urlParams.get('state');
        const querySubstepParam = urlParams.get('substep');
        const queryStepParam = urlParams.get('step');
        const openSetupGuideParam = urlParams.get('openSetupGuide');

        let step: number | undefined;
        let stateSubstep: number | undefined;
        if (stateParam) {
            try {
                const state = JSON.parse(decodeURIComponent(stateParam));
                if (typeof state.step === 'number') {
                    step = state.step;
                } else if (typeof state.step === 'string') {
                    const parsedStep = parseInt(state.step, 10);
                    if (!Number.isNaN(parsedStep)) {
                        step = parsedStep;
                    }
                }

                if (typeof state.substep === 'number') {
                    stateSubstep = state.substep;
                } else if (typeof state.substep === 'string') {
                    const parsedStateSubstep = parseInt(state.substep, 10);
                    if (!Number.isNaN(parsedStateSubstep)) {
                        stateSubstep = parsedStateSubstep;
                    }
                }
            } catch (e) {
                console.error("Invalid state param:", e);
            }
        }

        const parsedQueryStep = queryStepParam ? parseInt(queryStepParam, 10) : NaN;
        const queryStep = Number.isNaN(parsedQueryStep) ? undefined : parsedQueryStep;
        const resolvedStep = step ?? queryStep;
        const parsedQuerySubstep = querySubstepParam ? parseInt(querySubstepParam, 10) : NaN;
        const querySubstep = Number.isNaN(parsedQuerySubstep) ? undefined : parsedQuerySubstep;
        const resolvedSubstep = stateSubstep ?? querySubstep;

        if (resolvedStep === 2 && (resolvedSubstep === 1 || resolvedSubstep === 2 || resolvedSubstep === 3 || resolvedSubstep === 4)) {
            substepFromUrl.current = resolvedSubstep;

            if (resolvedSubstep === 1) {
                setActiveStep('environment');
            } else if (resolvedSubstep === 2) {
                setActiveStep('basic-config');
                setTimeout(() => {
                    substep2_2Ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }, 300);
            } else if (resolvedSubstep === 3) {
                setActiveStep('revenue-correlation');
            } else if (resolvedSubstep === 4) {
                setActiveStep('pause-fix');
            }

            if (resolvedSubstep === 2 && openSetupGuideParam === '1') {
                openSetupGuideAtStep3();
            }

            cleanupDeepLinkParams();
        }

        const hasExistingAccountId =
            formData.accountId !== '' ||
            loadedStripeData?.formData?.accountId ||
            config?.accountId ||
            sourceConfigurations.stripe?.accountId;

        if (hasExistingAccountId) {
            if (code) {
                cleanOAuthParams();
            }
            return;
        }

        if (step === 2 && code) {
            const handleStripeOAuth = async () => {
                const savedFormData = getStripeFormData();
                if (savedFormData) {
                    try {
                        const parsedData = JSON.parse(savedFormData);
                        setFormData(prev => ({
                            ...prev,
                            sourceName: parsedData.sourceName || prev.sourceName,
                            secretKey: parsedData.secretKey || prev.secretKey,
                            dataRetentionLocation: parsedData.dataRetentionLocation || prev.dataRetentionLocation,
                            groupIdMapping: parsedData.groupIdMapping || prev.groupIdMapping,
                            environmentId: parsedData.environmentId || prev.environmentId,
                            environmentName: parsedData.environmentName || prev.environmentName,
                            stripeEnvironment: parsedData.stripeEnvironment || prev.stripeEnvironment,
                            syncStartDate: parsedData.syncStartDate ? new Date(parsedData.syncStartDate) : prev.syncStartDate,
                            accountId: ""
                        }));
                    } catch (error) {
                        console.error('Error parsing saved form data:', error);
                    }
                }

                try {
                    console.log('OAuth code received:', code);
                    setAccountIdLoading(true);

                    const result = await GetStripeAccountDetails({
                        variables: {
                            input: {
                                accessCode: code
                            }
                        }
                    });

                    const accountId = result.data?.getStripeAccountDetails?.accountId;

                    setFormData(prev => ({
                        ...prev,
                        accountId: accountId,
                        accountIdEntryMode: accountId ? 'oauth' : prev.accountIdEntryMode
                    }));

                    setAccountIdLoading(false);
                    cleanOAuthParams();
                } catch (error) {
                    setAccountIdLoading(false);
                    console.error('Error processing OAuth code:', error);
                    cleanOAuthParams();
                    alert('Failed to connect Stripe account. Please try again.');
                }
            };

            handleStripeOAuth();
        }
    }, [loadedStripeData, config]);



    const stripeConfigHasData =
        config &&
        (config.accountId != null ||
            config.step4Completed ||
            (config.ingestionResult != null) ||
            (config.formData?.accountId != null));

    useEffect(() => {
        if (stripeConfigHasData) {
            setFormData(prev => ({
                ...prev,
                sourceName: config.sourceName ?? config.formData?.sourceName ?? "Stripe",
                accountId: config.accountId ?? config.formData?.accountId ?? "",
                accountIdEntryMode:
                    config.accountIdEntryMode ??
                    config.formData?.accountIdEntryMode ??
                    prev.accountIdEntryMode,
                stripeAccountName: config.stripeAccountName ?? config.formData?.stripeAccountName ?? prev.stripeAccountName,
                secretKey: config.secretKey ?? config.formData?.secretKey ?? "",
                dataRetentionLocation: config.dataRetentionLocation ?? config.formData?.dataRetentionLocation ?? "",
                syncStartDate: config.syncStartDate ?? config.formData?.syncStartDate
                    ? new Date(config.syncStartDate ?? config.formData.syncStartDate)
                    : getDefaultSyncDate(),
                groupIdMapping: config.groupIdMapping ?? config.formData?.groupIdMapping ?? false,
                environmentId: config.environmentId ?? config.formData?.environmentId ?? "",
                environmentName: config.environmentName ?? config.formData?.environmentName ?? "",
                stripeEnvironment: config.stripeEnvironment ?? config.formData?.stripeEnvironment ?? ""
            }));

            if (config?.testingSteps && config.testingSteps.length > 0) {
                setTestingSteps(config.testingSteps);
                const schemaStep = config.testingSteps.find((step: TestingStep) => step.id === 'schema');
                if (schemaStep?.customerMetaDataKeys?.length) {
                    setCustomerMetaDataKeys(schemaStep.customerMetaDataKeys);
                    setNoGroupIdWarningDismissed(!!findGroupIdKey(schemaStep.customerMetaDataKeys));
                    if (
                        schemaStep.selectedIdentifierKey &&
                        schemaStep.customerMetaDataKeys.includes(schemaStep.selectedIdentifierKey)
                    ) {
                        setSelectedAccountKey(schemaStep.selectedIdentifierKey);
                    } else {
                        setSelectedAccountKey(resolveSelectedAccountKey(schemaStep.customerMetaDataKeys));
                    }
                }
            }

            if (config.groupIdFound !== undefined) {
                setGroupIdFound(config.groupIdFound);
                setNoGroupIdWarningDismissed(config.groupIdFound);
            }

            if (config.ingestionResult) {
                setIngestionResult(config.ingestionResult);
                const status = config.ingestionResult.syncStatus;
                if (status === 'pending' || status === 'running' || status === 'succeeded' || status === 'failed') {
                    setSyncStatus(status);
                }
            }
            if (config.testCompleted !== undefined) setTestCompleted(config.testCompleted);
            if (config.isStep3Incomplete !== undefined) setIsStep3Incomplete(config.isStep3Incomplete);
            if (typeof config.isSetupIncomplete === 'boolean') setIsSetupIncomplete(config.isSetupIncomplete);
            if (config.step3Completed !== undefined) setStep3Completed(config.step3Completed);
            if (config.step4Completed !== undefined) {
                setStep4Completed(prev => {
                    if (config.step4Completed === true) return true;
                    if (
                        config.step4Completed === false &&
                        postSaveFlowMode === 'apiKeyOnly' &&
                        resyncStarted &&
                        prev
                    ) {
                        return true;
                    }
                    return config.step4Completed;
                });
            }

            if (!substepFromUrl.current) {
                if (userChosenStepRef.current) {
                    setActiveStep(userChosenStepRef.current);
                    userChosenStepRef.current = null;
                } else {
                    if (config.step4Completed) {
                        setActiveStep('pause-fix');
                    } else if (config.step3Completed) {
                        setActiveStep('revenue-correlation');
                    } else if (config.isSetupIncomplete) {
                        setActiveStep('basic-config');
                    } else if (config.step2Completed) {
                        setActiveStep('revenue-correlation');
                    } else if (config.step1Completed) {
                        setActiveStep('basic-config');
                    }
                }
            }

            if (config.notificationEmails) {
                const additionalEmails = Object.entries(config.notificationEmails)
                    .filter(([key]) => key !== 'userEmail')
                    .map(([_, value]) => value as string);
                emailManager.setEmails(additionalEmails);
            }
            substepFromUrl.current = null;
            return;
        }

        const hasSourceConfigData =
            sourceConfigurations?.stripe && Object.keys(sourceConfigurations.stripe).length > 0;
        if (didRestoreFromSavedConfigRef.current && hasSourceConfigData && !userChosenStepRef.current) {
            return;
        }

        if (loadedStripeData) {
            const urlParams = new URLSearchParams(window.location.search);
            const hasOAuthCode = urlParams.get('code');
            const localData = getStripeFormData();
            const hasLoadedAccountId = loadedStripeData.formData?.accountId;
            const formDataSource = hasSourceConfigData ? sourceConfigurations.stripe : loadedStripeData.formData;

            if (localData && hasOAuthCode && !hasLoadedAccountId) {
                try {
                    const ParsedData = JSON.parse(localData);
                    setFormData(prev => ({
                        ...prev,
                        sourceName: ParsedData.sourceName || formDataSource?.sourceName || "Stripe",
                        accountId: ParsedData.accountId || formDataSource?.accountId || "",
                        secretKey: ParsedData.secretKey || formDataSource?.secretKey || "",
                        syncStartDate: ParsedData.syncStartDate
                            ? new Date(ParsedData.syncStartDate)
                            : formDataSource?.syncStartDate
                                ? new Date(formDataSource.syncStartDate)
                                : getDefaultSyncDate(),
                        groupIdMapping: formDataSource?.groupIdMapping || false,
                        environmentId: formDataSource?.environmentId || "",
                        environmentName: formDataSource?.environmentName || "",
                        stripeEnvironment: formDataSource?.stripeEnvironment || ""
                    }));
                } catch (error) {
                    console.error('Error parsing localStorage:', error);
                }
            } else if (formDataSource) {
                setFormData(prev => ({
                    ...prev,
                    sourceName: formDataSource.sourceName || "Stripe",
                    accountId: formDataSource.accountId || "",
                    stripeAccountName: formDataSource.stripeAccountName ?? prev.stripeAccountName,
                    secretKey: formDataSource.secretKey || prev.secretKey,
                    dataRetentionLocation: formDataSource.dataRetentionLocation || "",
                    syncStartDate: formDataSource.syncStartDate
                        ? new Date(formDataSource.syncStartDate)
                        : getDefaultSyncDate(),
                    groupIdMapping: formDataSource.groupIdMapping || false,
                    environmentId: formDataSource.environmentId || "",
                    environmentName: formDataSource.environmentName || "",
                    stripeEnvironment: formDataSource.stripeEnvironment || ""
                }));
            }

            if (loadedStripeData.testingSteps && loadedStripeData.testingSteps.length > 0) {
                setTestingSteps(loadedStripeData.testingSteps);
                const schemaStep = loadedStripeData.testingSteps.find((step: TestingStep) => step.id === 'schema');
                if (schemaStep) {
                    if (schemaStep.customerMetaDataKeys && schemaStep.customerMetaDataKeys.length > 0) {
                        setCustomerMetaDataKeys(schemaStep.customerMetaDataKeys);
                        setNoGroupIdWarningDismissed(!!findGroupIdKey(schemaStep.customerMetaDataKeys));
                        if (
                            schemaStep.selectedIdentifierKey &&
                            schemaStep.customerMetaDataKeys.includes(schemaStep.selectedIdentifierKey)
                        ) {
                            setSelectedAccountKey(schemaStep.selectedIdentifierKey);
                        } else {
                            setSelectedAccountKey(resolveSelectedAccountKey(schemaStep.customerMetaDataKeys));
                        }
                    }
                }
            }

            if (loadedStripeData.testingSteps) {
                const schemaStep = loadedStripeData.testingSteps.find((step: TestingStep) => step.id === 'schema');
                if (schemaStep?.customerMetaDataKeys && schemaStep.customerMetaDataKeys.length > 0) {
                    const groupIdKey = findGroupIdKey(schemaStep.customerMetaDataKeys);
                    setNoGroupIdWarningDismissed(!!groupIdKey);
                    if (groupIdKey && !schemaStep.selectedIdentifierKey) setSelectedAccountKey(groupIdKey);
                }
            }

            if (loadedStripeData.groupIdFound !== undefined) {
                setGroupIdFound(loadedStripeData.groupIdFound);
                setNoGroupIdWarningDismissed(loadedStripeData.groupIdFound);
            }
            if (loadedStripeData?.testingSteps && loadedStripeData.testingSteps.length > 0) {
                setTestingSteps(loadedStripeData.testingSteps);
            }
            if (loadedStripeData.ingestionResult) {
                setIngestionResult(loadedStripeData.ingestionResult);
                const status = loadedStripeData.ingestionResult.syncStatus;
                if (status === 'pending' || status === 'running' || status === 'succeeded' || status === 'failed') {
                    setSyncStatus(status);
                }
            }
            if (loadedStripeData.testCompleted !== undefined) setTestCompleted(loadedStripeData.testCompleted);
            if (loadedStripeData.isStep3Incomplete !== undefined) setIsStep3Incomplete(loadedStripeData.isStep3Incomplete);
            if (typeof (loadedStripeData as { missingEntitiesAcknowledged?: boolean }).missingEntitiesAcknowledged === 'boolean') {
                const acknowledged = (loadedStripeData as { missingEntitiesAcknowledged: boolean }).missingEntitiesAcknowledged;
                setMissingEntitiesAcknowledged(acknowledged);
                setIsEntityErrorExpanded(!acknowledged);
            }
            if (typeof loadedStripeData.isSetupIncomplete === 'boolean') {
                setIsSetupIncomplete(loadedStripeData.isSetupIncomplete);
            }
            if (loadedStripeData.step3Completed !== undefined) setStep3Completed(loadedStripeData.step3Completed);
            const step4Source =
                hasSourceConfigData && typeof sourceConfigurations.stripe.step4Completed === 'boolean'
                    ? sourceConfigurations.stripe.step4Completed
                    : loadedStripeData.step4Completed;
            if (typeof step4Source === 'boolean') {
                setStep4Completed(prev => {
                    if (step4Source === true) return true;
                    if (
                        step4Source === false &&
                        postSaveFlowMode === 'apiKeyOnly' &&
                        resyncStarted &&
                        prev
                    ) {
                        return true;
                    }
                    return step4Source;
                });
            }

            if (!substepFromUrl.current) {
                if (userChosenStepRef.current) {
                    setActiveStep(userChosenStepRef.current);
                    userChosenStepRef.current = null;
                } else {
                    const stepData = hasSourceConfigData ? sourceConfigurations.stripe : loadedStripeData;
                    if (stepData.step4Completed) {
                        setActiveStep('pause-fix');
                    } else if (stepData.step3Completed) {
                        setActiveStep('revenue-correlation');
                    } else if (stepData.isSetupIncomplete) {
                        setActiveStep('basic-config');
                    } else if (stepData.step2Completed) {
                        setActiveStep('revenue-correlation');
                    } else if (stepData.step1Completed) {
                        setActiveStep('basic-config');
                    }
                }
            }

            if (loadedStripeData.notificationEmails) {
                const additionalEmails = Object.entries(loadedStripeData.notificationEmails)
                    .filter(([key]) => key !== 'userEmail')
                    .map(([_, value]) => value as string);
                emailManager.setEmails(additionalEmails);
            }
        }
    }, [loadedStripeData, config, sourceConfigurations, postSaveFlowMode, resyncStarted]);

    useEffect(() => {

        if (emailManager.state.emails.length > 0 && (loadedStripeData || config)) {
            const updatedNotificationEmails = {
                userEmail: getUser()?.email,
                additionalEmails: emailManager.state.emails
            };


            updateSourceConfiguration('stripe', {
                notificationEmails: updatedNotificationEmails
            });

            setConfig((prev: any) => ({
                ...prev,
                notificationEmails: updatedNotificationEmails
            }));
        }
    }, [emailManager.state.emails]);

    useEffect(() => {
        if (isTesting) {
            const interval = setInterval(() => {
                setDotCount(prev => prev >= 3 ? 1 : prev + 1);
            }, 500);

            return () => clearInterval(interval);
        }
    }, [isTesting]);

    useEffect(() => {
        fetchEnvironments()
    }, [])

    // Automatically set step4WasEverCompleted when step4Completed becomes true
    useEffect(() => {
        if (step4Completed && !step4WasEverCompleted) {
            setStep4WasEverCompleted(true);
        }
    }, [step4Completed, step4WasEverCompleted]);

    useEffect(() => {
        const savedValue = sourceConfigurations.stripe?.step4WasEverCompleted || 
                          loadedStripeData?.step4WasEverCompleted ||
                          sourceConfigurations.stripe?.step4CompletedAt ||
                          loadedStripeData?.step4CompletedAt;
        if (savedValue && !step4WasEverCompleted) {
            setStep4WasEverCompleted(true);
        }
    }, [
        sourceConfigurations.stripe?.step4WasEverCompleted, 
        loadedStripeData?.step4WasEverCompleted,
        sourceConfigurations.stripe?.step4CompletedAt,
        loadedStripeData?.step4CompletedAt,
        step4WasEverCompleted
    ]);

    useEffect(() => {
        if (isDemoMode) {
            setFormData((prev) => ({
                ...prev,
                environmentId: DEMO_ENVIRONMENT.id,
                environmentName: DEMO_ENVIRONMENT.displayName,
            }));
        } else {
            setFormData((prev) => ({
                ...prev,
                environmentId: `${environmentsData?.getEnvironmentsForProduct.environments.find(env => env?.displayName === getEnvironmentDisplayName())?.id}`,
                environmentName: `${environmentsData?.getEnvironmentsForProduct.environments.find(env => env?.displayName === getEnvironmentDisplayName())?.displayName}`,
            }));
        }
    }, [environmentsData, isDemoMode])

    const handleInputChange = (field: keyof FormData, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: undefined }));
        }
    };

    const handleNewEnvironment = () => {
        setShowEnvironmentDropdown(false);
        setShowNewEnvironmentForm(true);
    };

    const handleCreateNewEnvironment = async () => {
        if (!newEnvironmentForm.displayName) {
            return;
        }

        try {
            const result = await createEnvironmentCaMutation({
                variables: {
                    input: {
                        name: newEnvironmentForm.displayName.toLowerCase().replace(/\s+/g, '-'),
                        displayName: newEnvironmentForm.displayName,
                        environmentType: newEnvironmentForm.environmentType as EnvironmentType
                    }
                }
            });

            if (result.data?.createEnvironmentCA?.isSuccess) {
                setNewEnvironmentForm({
                    displayName: '',
                    environmentType: EnvironmentType.NonProduction
                });
                setShowNewEnvironmentForm(false);
                await fetchEnvironments();
            } else {
                console.error('Failed to create environment');
            }
        } catch (error) {
            console.error('Error creating environment:', error);
        }
    };

    const handleCancelNewEnvironment = () => {
        setShowNewEnvironmentForm(false);
        setNewEnvironmentForm({
            displayName: '',
            environmentType: EnvironmentType.NonProduction
        });
    };

    const resetSteps3And4 = (opts?: { keepIngestionResult?: boolean }) => {
        const keepIngestionResult = !!opts?.keepIngestionResult;
        setStep3Completed(false);
        setStep4Completed(false);
        setTestCompleted(false);
        setTestingSteps([
            {
                id: 'connection',
                title: 'Connection',
                loadingText: 'Establishing Connection',
                successText: 'Connected',
                errorText: 'Connection Error',
                status: 'pending'
            },
            {
                id: 'data',
                title: 'Data Retrieval',
                loadingText: 'Retrieving Data Tables',
                successText: 'Data Retrieved',
                errorText: 'Unable to Retrieve Data',
                status: 'pending'
            },
            {
                id: 'schema',
                title: 'Schema Verification',
                loadingText: 'Verifying Schema',
                successText: 'Schema Verified',
                errorText: 'Schema Error Detected',
                status: 'pending'
            }
        ]);
        setCustomerMetaDataKeys([]);
        setSelectedAccountKey('group_id');
        setGroupIdFound(false);
        if (!keepIngestionResult) {
            setIngestionResult(null);
        }
        setIngestionError(null);
        setShowIngestionLoadingSteps(false);
        setSyncStatus('pending');
        setIsSyncPolling(false);
        setLastSyncedDate(null);
        setIsSetupIncomplete(true);
    };

    const handleEditSourceName = () => {
        setOriginalSourceName(formData.sourceName);
        setIsEditingSourceName(true);
    };

    const handleCancelSourceName = () => {
        handleInputChange('sourceName', originalSourceName);
        setIsEditingSourceName(false);
        setOriginalSourceName('');
    };

    const handleSaveSourceName = () => {
        setIsEditingSourceName(false);
        setOriginalSourceName('');
        const cfg = {
            ...formData,
            step1Completed: !!(formData.environmentId && formData.stripeEnvironment),
            step2Completed: !!(formData.sourceName && formData.accountId && formData.secretKey && formData.syncStartDate)
        };
        updateSourceConfiguration('stripe', cfg);
        setConfig(cfg);
        onAutoSave();
    };

    const handleEditSecretKey = () => {
        setOriginalSecretKey(formData.secretKey);
        setIsEditingSecretKey(true);
    };

    const handleCancelSecretKey = () => {
        handleInputChange('secretKey', originalSecretKey);
        setIsEditingSecretKey(false);
        setOriginalSecretKey('');
        setPendingSecretKeyChange(false);
    };

    const handleSaveSecretKey = () => {
        let shouldReset = false;
        if (formData.secretKey !== originalSecretKey) {
            resetSteps3And4({ keepIngestionResult: true });
            shouldReset = true;
        }
        setIsEditingSecretKey(false);
        setOriginalSecretKey('');
        if (shouldReset && isBasicConfigFieldsLocked) {
            setPendingSecretKeyChange(true);
        }

        const preservedIngestionResult = sourceConfigurations.stripe?.ingestionResult || ingestionResult || null;
        const step1Complete = !!(formData.environmentId && formData.stripeEnvironment);
        const step2Complete = !!(formData.sourceName && formData.accountId && formData.secretKey && formData.syncStartDate);
        const step3Complete = shouldReset ? false : step3Completed;
        const step4Complete = shouldReset ? false : step4Completed;
        
        const cfg = {
            ...formData,
            step1Completed: step1Complete,
            step2Completed: step2Complete,
            step3Completed: step3Complete,
            step4Completed: step4Complete,
            step4WasEverCompleted: step4WasEverCompleted,
            isStep3Incomplete: shouldReset ? false : isStep3Incomplete,
            // Ensure UI doesn't re-hydrate old Step-3 schema state after a credential change.
            customerMetaDataKeys: shouldReset ? [] : customerMetaDataKeys,
            selectedIdentifierKey: shouldReset ? DEFAULT_ACCOUNT_IDENTIFIER_KEY : selectedAccountKey,
            groupIdFound: shouldReset ? false : groupIdFound,
            groupIdMapping: shouldReset ? false : formData.groupIdMapping,
            testingSteps: shouldReset
                ? [
                      {
                          id: 'connection',
                          title: 'Connection',
                          loadingText: 'Establishing Connection',
                          successText: 'Connected',
                          errorText: 'Connection Error',
                          status: 'pending' as const
                      },
                      {
                          id: 'data',
                          title: 'Data Retrieval',
                          loadingText: 'Retrieving Data Tables',
                          successText: 'Data Retrieved',
                          errorText: 'Unable to Retrieve Data',
                          status: 'pending' as const
                      },
                      {
                          id: 'schema',
                          title: 'Schema Verification',
                          loadingText: 'Verifying Schema',
                          successText: 'Schema Verified',
                          errorText: 'Schema Error Detected',
                          status: 'pending' as const
                      }
                  ]
                : testingSteps,
            testCompleted: shouldReset ? false : testCompleted,
            schemaVerified: sourceConfigurations.stripe?.schemaVerified || false,
            status: sourceConfigurations.stripe?.status || 'pending',
            completed: step1Complete && step2Complete && step3Complete && step4Complete,
            isSetupIncomplete: shouldReset,
            ingestionResult: preservedIngestionResult
        };
        updateSourceConfiguration('stripe', cfg);
        setConfig(cfg);
        onAutoSave();
    };

    const handleEditSyncDate = () => {
        setOriginalSyncDate(formData.syncStartDate instanceof Date ? new Date(formData.syncStartDate) : null);
        setIsEditingSyncDate(true);
    };

    const handleCancelSyncDate = () => {
        if (originalSyncDate) {
            handleInputChange('syncStartDate', originalSyncDate);
        }
        setIsEditingSyncDate(false);
        setOriginalSyncDate(null);
        setShowCalendar(false);
        setPendingSyncDateChange(false);
    };

    const handleSaveSyncDate = () => {
        let didChange = false;
        if (originalSyncDate && formData.syncStartDate instanceof Date) {
            didChange = formData.syncStartDate.toISOString() !== originalSyncDate.toISOString();
        }
        const shouldReset = !!(didChange && isBasicConfigFieldsLocked);
        if (shouldReset) {
            resetSteps3And4({ keepIngestionResult: true });
            setPendingSyncDateChange(true);
            setBasicConfigSavedThisSession(false);
        }
        setIsEditingSyncDate(false);
        setOriginalSyncDate(null);
        setShowCalendar(false);
        const step2FieldsComplete = !!(
            formData.sourceName &&
            formData.accountId &&
            formData.secretKey &&
            formData.syncStartDate
        );
        const preservedIngestionResult = sourceConfigurations.stripe?.ingestionResult || ingestionResult || null;
        const cfg = {
            ...formData,
            step1Completed: !!(formData.environmentId && formData.stripeEnvironment),
            step2Completed: (shouldReset && isBasicConfigFieldsLocked) ? false : step2FieldsComplete,
            step3Completed: shouldReset ? false : step3Completed,
            step4Completed: shouldReset ? false : step4Completed,
            step4WasEverCompleted: step4WasEverCompleted,
            isStep3Incomplete: shouldReset ? false : isStep3Incomplete,
            customerMetaDataKeys: shouldReset ? [] : customerMetaDataKeys,
            selectedIdentifierKey: shouldReset ? DEFAULT_ACCOUNT_IDENTIFIER_KEY : selectedAccountKey,
            groupIdFound: shouldReset ? false : groupIdFound,
            groupIdMapping: shouldReset ? false : formData.groupIdMapping,
            testingSteps: shouldReset
                ? [
                      {
                          id: 'connection',
                          title: 'Connection',
                          loadingText: 'Establishing Connection',
                          successText: 'Connected',
                          errorText: 'Connection Error',
                          status: 'pending' as const
                      },
                      {
                          id: 'data',
                          title: 'Data Retrieval',
                          loadingText: 'Retrieving Data Tables',
                          successText: 'Data Retrieved',
                          errorText: 'Unable to Retrieve Data',
                          status: 'pending' as const
                      },
                      {
                          id: 'schema',
                          title: 'Schema Verification',
                          loadingText: 'Verifying Schema',
                          successText: 'Schema Verified',
                          errorText: 'Schema Error Detected',
                          status: 'pending' as const
                      }
                  ]
                : testingSteps,
            testCompleted: shouldReset ? false : testCompleted,
            schemaVerified: sourceConfigurations.stripe?.schemaVerified || false,
            status: sourceConfigurations.stripe?.status || 'pending',
            completed: shouldReset ? false : (
                !!(formData.environmentId && formData.stripeEnvironment) &&
                step2FieldsComplete &&
                step3Completed &&
                step4Completed
            ),
            isSetupIncomplete: shouldReset,
            ingestionResult: preservedIngestionResult
        };
        updateSourceConfiguration('stripe', cfg);
        setConfig(cfg);
        onAutoSave();
    };

    const saveConfiguration = (
        actionRequired: boolean,
        overrides?: {
            testingSteps?: TestingStep[];
            testCompleted?: boolean;
            selectedIdentifierKey?: string;
            customerMetaDataKeys?: string[];
            groupIdFound?: boolean;
            groupIdMapping?: boolean;
            missingEntitiesAcknowledged?: boolean;
        }
    ) => {
    const stepsToSave = overrides?.testingSteps ?? testingSteps;
    const testCompletedToSave = overrides?.testCompleted ?? testCompleted;
    const selectedIdentifierKeyToSave = overrides?.selectedIdentifierKey ?? selectedAccountKey;
    const customerMetaDataKeysToSave = overrides?.customerMetaDataKeys ?? customerMetaDataKeys;
    const groupIdFoundToSave = overrides?.groupIdFound ?? groupIdFound;
    const groupIdMappingToSave = overrides?.groupIdMapping ?? (selectedIdentifierKeyToSave ? true : formData.groupIdMapping);
    const missingEntitiesAcknowledgedToSave = overrides?.missingEntitiesAcknowledged ?? missingEntitiesAcknowledged;
    const prevStripe = sourceConfigurations?.stripe ?? {};
    const ingestionToPersist =
        ingestionResult ?? prevStripe.ingestionResult ?? null;
    const step2ShouldBeCompleted = prevStripe.step2Completed === true 
        ? true 
        : !!(formData.sourceName && formData.accountId && formData.secretKey && formData.syncStartDate);
    
    const config = {
        ...formData,
        groupIdMapping: groupIdMappingToSave,
        step1Completed: !!(formData.environmentId && formData.stripeEnvironment),
        step2Completed: step2ShouldBeCompleted,
        step3Completed: true,
        step3CompletedAt: new Date().toISOString(),
        isStep3Incomplete: actionRequired,
        step4Completed,
        step4CompletedAt: step4Completed
            ? prevStripe.step4CompletedAt || new Date().toISOString()
            : undefined,
        ingestionResult: ingestionToPersist,
        testingSteps: stepsToSave.map(step => ({
            ...step,
            ...(step.id === 'schema' ? {
                selectedIdentifierKey: selectedIdentifierKeyToSave,
                customerMetaDataKeys: customerMetaDataKeysToSave
            } : {})
        })),
        testCompleted: testCompletedToSave,
        schemaVerified: true,
        selectedIdentifierKey: selectedIdentifierKeyToSave,
        customerMetaDataKeys: customerMetaDataKeysToSave,
        groupIdFound: groupIdFoundToSave,
        missingEntitiesAcknowledged: missingEntitiesAcknowledgedToSave,
        status: prevStripe.status || 'pending',
        completed: !!(formData.environmentId && formData.stripeEnvironment) &&
                   step2ShouldBeCompleted &&
                   true && // step3Completed (set to true above)
                   step4Completed,
        isSetupIncomplete: false
    };
    updateSourceConfiguration("stripe", config);
    setConfig(config);
};

    const saveCurrentStepConfiguration = () => {
        const prevStripe = sourceConfigurations?.stripe ?? {};
        const step2ShouldBeCompleted = prevStripe.step2Completed === true 
            ? true 
            : !!(formData.sourceName && formData.accountId && formData.secretKey && formData.syncStartDate);
        
        const config = {
            ...formData,
            step1Completed: !!(formData.environmentId && formData.stripeEnvironment),
            step2Completed: step2ShouldBeCompleted,
            step3Completed: step3Completed,
            step4Completed: step4Completed,
            isStep3Incomplete: isStep3Incomplete,
            testingSteps,
            testCompleted,
            schemaVerified: sourceConfigurations.stripe?.schemaVerified || false,
            status: sourceConfigurations.stripe?.status || 'pending',
            completed: !!(formData.environmentId && formData.stripeEnvironment) &&
                       step2ShouldBeCompleted &&
                       step3Completed &&
                       step4Completed,
            isSetupIncomplete: isSetupIncomplete
        };
        updateSourceConfiguration("stripe", config);
        setConfig(config);
        if (config.step2Completed) {
            removeStripeFormData();
        }
    };


    const handleProceedAnyway = () => {
        setSelectedOption("proceed");
        setStep3Completed(true);
        setIsStep3Incomplete(true);
        userChosenStepRef.current = 'pause-fix';
        setActiveStep('pause-fix');
    };


    const handleInviteTeam = (team: TeamId) => {
        setSelectedTeam(team);
        setShowInviteModal(true);
        setIsStep3Incomplete(true);
        setStep3Completed(true);

        saveConfiguration(true);

        userChosenStepRef.current = 'pause-fix';
        setActiveStep('pause-fix');
        onAutoSave();
    };

    const onSubmit = (e: React.FormEvent) => {
        e.preventDefault();
    };

    const formatDate = (date: Date): string => {
        return date.toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    };

    const completedSteps = stepsConfig.filter(step => step.isCompleted).length;
    const totalSteps = stepsConfig.length;
    const progressPercentage = (completedSteps / totalSteps) * 100;

    const [calendarMonth, setCalendarMonth] = useState(
        formData.syncStartDate instanceof Date ? formData.syncStartDate.getMonth() : new Date().getMonth()
    );
    const [calendarYear, setCalendarYear] = useState(
        formData.syncStartDate instanceof Date ? formData.syncStartDate.getFullYear() : new Date().getFullYear()
    );

    const renderCalendar = () => {
        const currentDate = new Date();

        const firstDay = new Date(calendarYear, calendarMonth, 1);
        const lastDay = new Date(calendarYear, calendarMonth + 1, 0);
        const firstDayOfWeek = firstDay.getDay();
        const daysInMonth = lastDay.getDate();

        const days = [];

        for (let i = 0; i < firstDayOfWeek; i++) {
            days.push(<div key={`empty-${i}`} className="p-2"></div>);
        }

        for (let day = 1; day <= daysInMonth; day++) {
            const date = new Date(calendarYear, calendarMonth, day);
            const isSelected = formData.syncStartDate instanceof Date ? formData.syncStartDate.toDateString() === date.toDateString() : false;
            const isDisabled = date > currentDate || date < new Date("2020-01-01");

            days.push(
                <button
                    key={day}
                    type="button"
                    onClick={() => {
                        if (!isDisabled) {
                            handleInputChange('syncStartDate', date);
                            setShowCalendar(false);
                        }
                    }}
                    disabled={isDisabled}
                    className={`p-2 text-sm rounded hover:bg-gray-100 ${isSelected ? 'bg-blue-600 text-white hover:bg-blue-700' : ''
                        } ${isDisabled ? 'text-gray-400 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                    {day}
                </button>
            );
        }

        const handlePreviousMonth = () => {
            if (calendarMonth === 0) {
                setCalendarMonth(11);
                setCalendarYear(calendarYear - 1);
            } else {
                setCalendarMonth(calendarMonth - 1);
            }
        };

        const handleNextMonth = () => {
            if (calendarMonth === 11) {
                setCalendarMonth(0);
                setCalendarYear(calendarYear + 1);
            } else {
                setCalendarMonth(calendarMonth + 1);
            }
        };

        const months = [
            'January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'
        ];

        const years = [];
        for (let year = 2020; year <= currentDate.getFullYear(); year++) {
            years.push(year);
        }

        return (
            <div className="absolute z-10 mb-2 bottom-full bg-white border border-gray-300 rounded-md shadow-lg p-4 w-80">
                <div className="flex items-center justify-between mb-4 gap-2">
                    <button
                        type="button"
                        onClick={handlePreviousMonth}
                        className="p-1 hover:bg-gray-100 rounded"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>

                    <div className="flex gap-2">
                        <select
                            value={calendarMonth}
                            onChange={(e) => setCalendarMonth(parseInt(e.target.value))}
                            className="text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            {months.map((month, index) => (
                                <option key={month} value={index}>{month}</option>
                            ))}
                        </select>

                        <select
                            value={calendarYear}
                            onChange={(e) => setCalendarYear(parseInt(e.target.value))}
                            className="text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            {years.map(year => (
                                <option key={year} value={year}>{year}</option>
                            ))}
                        </select>
                    </div>

                    <button
                        type="button"
                        onClick={handleNextMonth}
                        className="p-1 hover:bg-gray-100 rounded"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                    </button>
                </div>
                <div className="grid grid-cols-7 gap-1 text-xs mb-2">
                    {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
                        <div key={day} className="p-2 text-center font-medium text-gray-500">{day}</div>
                    ))}
                </div>
                <div className="grid grid-cols-7 gap-1">
                    {days}
                </div>
            </div>
        );
    };

    const getConnectorLineStyle = () => {
        if (step2ContainerRef.current && step3ContainerRef.current && step4ContainerRef.current) {
            const step2Height = step2ContainerRef.current.getBoundingClientRect().height;
            const step3Height = step3ContainerRef.current.getBoundingClientRect().height;
            const step4Height = step4ContainerRef.current.getBoundingClientRect().height;

            if (activeStep === 'pause-fix') {
                return {
                    top: '4rem',
                    bottom: `${step4Height - 10}px`,
                };
            } else if (activeStep === 'revenue-correlation') {
                return {
                    top: '4rem',
                    bottom: `${step3Height - 10}px`,
                };
            } else if (activeStep === 'basic-config') {
                return {
                    top: '4rem',
                    bottom: `${step2Height - 10}px`,
                };
            }
        }
        return {
            top: '4rem',
            bottom: '4rem',
        };
    };


    const productName = getProductName() || "";

    const getInitials = (name: string) => {
        if (!name || name.trim() === "") return "";

        const words = name.trim().split(/\s+/);
        if (words.length === 1) {
            return words[0].substring(0, 2).toUpperCase();
        }
        return (words[0][0] + words[1][0]).toUpperCase();
    };

    const initials = getInitials(productName);

    const handleAccountIDSelect = () => {
        setStripeFormData(JSON.stringify(formData));
        localRef.current = true

        const baseUrl = window.location.origin + window.location.pathname;
        const state = { step: 2, substep: 2 };

        window.location.href =
            `https://connect.stripe.com/oauth/v2/authorize` +
            `?response_type=code` +
            `&client_id=${process.env.STRIPE_CLIENT_ID}` +
            `&scope=read_write` +
            `&redirect_uri=${encodeURIComponent(baseUrl)}` +
            `&state=${encodeURIComponent(JSON.stringify(state))}`;



    }

    const renderEnvironmentDiagram = (isCompleted: boolean) => {
        if (!formData.environmentId || !formData.stripeEnvironment) return null;

        return (
            <div className="flex items-center justify-center space-x-3 py-3">
                {/* Stripe Environment */}
                <div className="flex flex-col items-center space-y-1 group relative">
                    <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
                        Your Stripe
                    </div>
                    <div className="flex items-center space-x-2 cursor-help">
                        <div className="w-6 h-6 rounded-full border-2 border-blue-600 flex items-center justify-center bg-white">
                            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none">
                                <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-2.808 0-5.671-1.226-7.59-2.335l-.928 5.494C4.926 22.41 8.03 24 11.89 24c2.606 0 4.655-.587 6.148-1.688 1.572-1.165 2.369-2.874 2.369-5.083 0-4.359-2.64-6.165-6.431-7.374z" fill="#635BFF" />
                            </svg>
                        </div>
                        <div className="text-xs font-semibold text-gray-700">
                            {formData.stripeEnvironment.charAt(0).toUpperCase() + formData.stripeEnvironment.slice(1).toLowerCase()}
                        </div>
                    </div>
                    {/* Tooltip */}
                    <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 px-3 py-1.5 bg-gray-900 text-white text-xs rounded shadow-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                        Your Stripe environment: {formData.stripeEnvironment.charAt(0).toUpperCase() + formData.stripeEnvironment.slice(1).toLowerCase()}
                        <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                    </div>
                </div>

                {/* Connection Visual */}
                <div className="flex items-center space-x-1 pt-4">
                    <div className="w-6 h-0.5 bg-blue-600"></div>
                    <div className="w-6 h-6 bg-transparent rounded-full flex items-center justify-center border-2" style={{ borderColor: isCompleted ? '#16a34a' : '#eab308' }}>
                        {isCompleted ? (
                            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                        ) : (
                            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="#eab308" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="12" y1="8" x2="12" y2="12"></line>
                                <line x1="12" y1="16" x2="12.01" y2="16"></line>
                            </svg>
                        )}
                    </div>
                    <svg className="w-8 h-3" viewBox="0 0 32 12" fill="none">
                        <path d="M0 6 L26 6" stroke="#2563eb" strokeWidth="2" />
                        <path d="M22 2 L30 6 L22 10" stroke="#2563eb" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </div>

                {/* ThriveStack Environment */}
                <div className="flex flex-col items-center space-y-1 group relative">
                    <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
                        {productName || "Product"}
                    </div>
                    <div className="flex items-center space-x-2 cursor-help">
                        <div className="w-6 h-6 rounded-full flex items-center justify-center border-2 border-blue-600 bg-white">
                            <span className="text-blue-600 text-[9px] font-bold">{initials}</span>
                        </div>
                        <div className="text-xs font-semibold text-gray-700 max-w-20 truncate">
                            {formData.environmentName.charAt(0).toUpperCase() + formData.environmentName.slice(1).toLowerCase()}
                        </div>
                    </div>
                    {/* Tooltip */}
                    <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 px-3 py-1.5 bg-gray-900 text-white text-xs rounded shadow-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                        Your Stripe data is syncing to your {formData.environmentName.charAt(0).toUpperCase() + formData.environmentName.slice(1).toLowerCase()} <br /> environment within ThriveStack for "{productName}". Status: {isCompleted ? 'Successful' : 'Pending'}
                        <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                    </div>
                </div>
            </div>
        );
    };

    const renderEnvironmentContent = (step: StepConfig) => (
        <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Stripe Environment
                    </label>
                    <div className="relative" data-dropdown="stripe-environment">
                        <button
                            type="button"
                            onClick={() => setShowStripeEnvironmentDropdown(!showStripeEnvironmentDropdown)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-left flex items-center justify-between"
                        >
                            <span className={formData.stripeEnvironment ? 'text-gray-900 font-semibold' : 'text-gray-500'}>
                                {formData.stripeEnvironment === 'live' ? 'Live' :
                                    formData.stripeEnvironment === 'test' ? 'Test' :
                                        'Select Stripe Environment'}
                            </span>
                            <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showStripeEnvironmentDropdown ? 'rotate-180' : ''}`} />
                        </button>

                        {showStripeEnvironmentDropdown && (
                            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
                                <div className="py-1">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            handleInputChange('stripeEnvironment', 'live');
                                            setShowStripeEnvironmentDropdown(false);
                                        }}
                                        className="w-full px-3 py-2 text-left text-sm hover:bg-blue-50 hover:text-blue-900 flex items-center justify-between"
                                    >
                                        <span>Live</span>
                                        {formData.stripeEnvironment === 'live' && (
                                            <CheckCircle className="w-4 h-4 text-blue-600" />
                                        )}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            handleInputChange('stripeEnvironment', 'test');
                                            setShowStripeEnvironmentDropdown(false);
                                        }}
                                        className="w-full px-3 py-2 text-left text-sm hover:bg-blue-50 hover:text-blue-900 flex items-center justify-between"
                                    >
                                        <span>Test</span>
                                        {formData.stripeEnvironment === 'test' && (
                                            <CheckCircle className="w-4 h-4 text-blue-600" />
                                        )}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        ThriveStack Environment
                    </label>
                    <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-left flex items-center justify-between cursor-not-allowed">
                        <span className="text-gray-900 font-semibold">
                            {formData.environmentName ?
                                formData.environmentName.charAt(0).toUpperCase() + formData.environmentName.slice(1).toLowerCase() :
                                'Development'
                            }
                        </span>
                        <ChevronDown className="w-4 h-4 text-gray-400" />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                        To change this, change the environment from the navigation bar
                    </p>
                </div>
            </div>
            {/* Next Button for Step 1 */}
            <div className="flex justify-end pt-4 border-t border-gray-200">
                <button
                    type="button"
                    onClick={() => {
                        saveCurrentStepConfiguration();
                        userChosenStepRef.current = 'basic-config';
                        setActiveStep('basic-config');
                        setTimeout(() => {
                            step2ContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }, 100);
                        onAutoSave()
                    }}
                    disabled={!(formData.environmentId && formData.stripeEnvironment)}
                    className={`px-6 py-2 rounded-md font-medium transition-colors ${formData.environmentId && formData.stripeEnvironment
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                >
                    Next
                </button>
            </div>
        </div>
    );

    const renderBasicConfigContent = () => {
        const isStep2Complete = !!(formData.sourceName && formData.accountId && formData.secretKey && formData.syncStartDate);
        // Lock account ID only after Step 2 is explicitly persisted via Save.
        const isAccountLocked = isBasicConfigFieldsLocked && !!formData.accountId;
        const isManualAccountMode = formData.accountIdEntryMode === 'manual';
        const isOauthAccountMode = formData.accountIdEntryMode === 'oauth';
        const shouldShowManualAccountInput = isManualAccountMode || (isAccountLocked && !isOauthAccountMode);

        return (
            <div className="space-y-8">
                {/* Source Name Section */}
                <div>
                    <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">2.1 Source Name</h3>
                        {formData.sourceName && (
                            <CheckCircle className="w-5 h-5 text-green-600" />
                        )}
                    </div>
                    <div className="ml-7">
                        <p className="text-sm text-gray-600 mb-4">Give this data source a memorable name to easily identify it in your dashboard.</p>
                        <div className="flex items-center gap-2">
                            <input
                                type="text"
                                placeholder="Enter source name"
                                value={formData.sourceName}
                                onChange={(e) => handleInputChange('sourceName', e.target.value)}
                                readOnly={isBasicConfigFieldsLocked && !!sourceConfigurations.stripe?.sourceName && !isEditingSourceName}
                                className={`w-96 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                    isBasicConfigFieldsLocked && !isEditingSourceName && !!sourceConfigurations.stripe?.sourceName
                                        ? 'bg-gray-50 cursor-not-allowed'
                                        : ''
                                }`}
                            />
                            {isBasicConfigFieldsLocked && sourceConfigurations.stripe?.sourceName && !isEditingSourceName && (
                                <button
                                    type="button"
                                    onClick={handleEditSourceName}
                                    className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md transition-colors"
                                    title="Edit"
                                >
                                    <Pencil className="w-4 h-4" />
                                </button>
                            )}
                            {isEditingSourceName && (
                                <div className="flex gap-2">
                                    <button
                                        type="button"
                                        onClick={handleSaveSourceName}
                                        className="px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors whitespace-nowrap"
                                    >
                                        Save
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleCancelSourceName}
                                        className="px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors whitespace-nowrap"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            )}
                        </div>
                        {errors.sourceName && <p className="text-red-600 text-sm mt-1">{errors.sourceName}</p>}
                    </div>
                </div>

                {/* Select Stripe Account Section */}
                <div ref={substep2_2Ref}>
                    <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">2.2 Select Stripe Account</h3>
                        {formData.accountId && (
                            <CheckCircle className="w-5 h-5 text-green-600" />
                        )}
                    </div>
                    <div className="ml-7">
                        <p className="text-sm text-gray-600 mb-4">Connect to the Stripe account you want to sync payment data from.</p>
                        {accountIdLoading && <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />}
                        {!accountIdLoading && (shouldShowManualAccountInput ? (
                            <div className="space-y-3">
                                <div className="relative flex items-center gap-2">
                                    <div className="flex items-center max-w-md">
                                        {isAccountLocked ? (
                                            <div
                                                className="w-64 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 cursor-not-allowed select-none text-gray-900 font-semibold"
                                                onCopy={(e) => e.preventDefault()}
                                                onCut={(e) => e.preventDefault()}
                                                onDragStart={(e) => e.preventDefault()}
                                                onMouseDown={(e) => e.preventDefault()}
                                            >
                                                {formData.accountId || '—'}
                                            </div>
                                        ) : (
                                            <input
                                                type="text"
                                                placeholder="Enter Stripe account ID"
                                                value={formData.accountId || ''}
                                                onChange={(e) => handleInputChange('accountId', e.target.value)}
                                                className="w-64 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            />
                                        )}
                                    </div>
                                    {isAccountLocked ? (
                                        <div className="flex items-center gap-2">
                                            <div className="relative group">
                                                <Lock className="w-4 h-4 text-gray-500" />
                                                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                                                    Stripe account ID is locked after you complete Basic Configuration.
                                                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                                                </div>
                                            </div>
                                            <div className="relative group">
                                                <Info className="w-4 h-4 text-gray-500 cursor-help" />
                                                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10 w-80">
                                                    To use a different Stripe account ID, <a href="#" onClick={(e) => { e.preventDefault(); onOpenIntegrationsList(); }} className="font-semibold underline">go to Integrations list</a>, delete this connection, and create a new one.
                                                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="flex items-center gap-4">
                                            <div className="relative group">
                                                <Info className="w-4 h-4 text-blue-500 cursor-help" onClick={openSetupGuideAtAccountId} />
                                                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                                                    Click to see how to find your Account ID
                                                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        accountId: '',
                                                        accountIdEntryMode: undefined,
                                                        stripeAccountName: undefined
                                                    }));
                                                }}
                                                className="text-sm text-blue-600 hover:text-blue-800 underline whitespace-nowrap"
                                            >
                                                Use OAuth instead
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : (formData.accountId ? (
                            <div className="space-y-3">
                                <button
                                    type="button"
                                    className="px-4 py-2 bg-green-50 border border-green-200 text-green-700 rounded-md flex items-center gap-2"
                                    disabled
                                >
                                    <CheckCircle className="h-4 w-4" />
                                    Account Selected
                                </button>

                                <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
                                    <div className="text-sm text-gray-700">
                                        <div className="font-medium">{formData.stripeAccountName || 'Stripe Account'}</div>
                                        <div className="mt-1 flex flex-wrap items-center gap-2">
                                            <span>Account ID: {formData.accountId}</span>
                                            {isAccountLocked ? (
                                                <div className="flex items-center gap-2">
                                                    <div className="relative group">
                                                        <Lock className="w-4 h-4 text-gray-500" />
                                                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                                                            Stripe account is locked after you complete Basic Configuration.
                                                            <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                                                        </div>
                                                    </div>
                                                    <div className="relative group">
                                                        <Info className="w-4 h-4 text-gray-500 cursor-help" />
                                                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10 w-80">
                                                            To use a different Stripe account, <a href="#" onClick={(e) => { e.preventDefault(); onOpenIntegrationsList(); }} className="font-semibold underline">go to Integrations list</a>, delete this connection, and create a new one.
                                                            <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : (
                                                <>
                                                    <button
                                                        onClick={() => {
                                                            if (isDemoMode) {
                                                                setShowDemoModal(true);
                                                            } else {
                                                                handleAccountIDSelect();
                                                            }
                                                        }}
                                                        type="button"
                                                        className="text-blue-600 underline hover:text-blue-800"
                                                    >
                                                        Reconnect
                                                    </button>
                                                    <button
                                                        type="button"
                                                        disabled
                                                        className="text-gray-400 cursor-not-allowed"
                                                    >
                                                        Enter account ID manually
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="flex items-center gap-3 flex-wrap">
                                <button
                                    onClick={() => {
                                        if (isDemoMode) {
                                            setShowDemoModal(true);
                                        } else {
                                            handleAccountIDSelect();
                                        }
                                    }}
                                    disabled={isDemoMode ? false : (!formData.sourceName || accountIdLoading)}
                                    className="flex items-center justify-center space-x-2 px-4 py-2.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                                >
                                    {accountIdLoading ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            <span>Connecting...</span>
                                        </>
                                    ) : (
                                        <>
                                            <SiStripe className="w-4 h-4" />
                                            <span>Select Account</span>
                                        </>
                                    )}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setFormData(prev => ({
                                            ...prev,
                                            accountIdEntryMode: 'manual',
                                            stripeAccountName: undefined
                                        }));
                                    }}
                                    disabled={isOauthAccountMode}
                                    className={`text-sm underline ${
                                        isOauthAccountMode
                                            ? 'text-gray-400 cursor-not-allowed'
                                            : 'text-blue-600 hover:text-blue-800'
                                    }`}
                                >
                                    Enter account ID manually
                                </button>
                            </div>
                        )))}
                    </div>
                </div>

                {/* Secret Key Section */}
                <div ref={secretKeySectionRef}>
                    <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">2.3 Secret Key</h3>
                        {formData.secretKey && (
                            <CheckCircle className="w-5 h-5 text-green-600" />
                        )}
                        <div className="relative group">
                            <Info className="h-4 w-4 text-gray-400 cursor-help" />
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                                Get your secret key from Stripe dashboard and paste it here for API access
                                <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                            </div>
                        </div>
                    </div>
                    <div className="ml-7">
                        <div className="flex items-center gap-2 mb-4">
                            <p className="text-sm text-gray-600">Your Stripe secret key allows ThriveStack to securely access your payment data via the Stripe API.</p>
                            <a
                                href="#"
                                onClick={(e) => {
                                    e.preventDefault();
                                    openSetupGuideAtStep3();
                                }}
                                className="text-blue-600 underline text-sm cursor-pointer whitespace-nowrap"
                            >
                                Show Documentation
                            </a>
                        </div>

                        <div className="space-y-3 mb-4">
                            <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-700">1. Get your key from Stripe</span>
                                <a
                                    href={formData.accountId
                                        ? `https://dashboard.stripe.com/${formData.accountId}/${formData.stripeEnvironment === 'test' ? 'test/' : ''}apikeys`
                                        : 'https://dashboard.stripe.com/apikeys'
                                    }
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 underline text-sm flex items-center gap-1"
                                >
                                    Get your key from Stripe
                                    <ExternalLink className="h-3 w-3" />
                                </a>
                            </div>
                            <div className="text-sm text-gray-700">2. Paste it below</div>
                        </div>

                        <div className="flex items-center gap-2 flex-wrap">
                            <div className="relative w-full max-w-md">
                            <input
                                type={showSecretKey ? "text" : "password"}
                                placeholder="sk_test_xx••••XXX"
                                value={formData.secretKey}
                                onChange={(e) => handleInputChange('secretKey', e.target.value)}
                                readOnly={isBasicConfigFieldsLocked && !!sourceConfigurations.stripe?.secretKey && !isEditingSecretKey}
                                className={`w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                    isBasicConfigFieldsLocked && !isEditingSecretKey && !!sourceConfigurations.stripe?.secretKey
                                        ? 'bg-gray-50 cursor-not-allowed'
                                        : ''
                                }`}
                            />
                            <button
                                type="button"
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                onClick={() => setShowSecretKey(!showSecretKey)}
                            >
                                {showSecretKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                            </div>
                            {isBasicConfigFieldsLocked && sourceConfigurations.stripe?.secretKey && !isEditingSecretKey && (
                                <button
                                    type="button"
                                    onClick={handleEditSecretKey}
                                    className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md transition-colors"
                                    title="Edit"
                                >
                                    <Pencil className="w-4 h-4" />
                                </button>
                            )}
                            {isEditingSecretKey && (
                                <div className="flex gap-2">
                                    <button
                                        type="button"
                                        onClick={handleSaveSecretKey}
                                        className="px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors whitespace-nowrap"
                                    >
                                        Save
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleCancelSecretKey}
                                        className="px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors whitespace-nowrap"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            )}
                        </div>
                        {isEditingSecretKey && (
                            <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                                <p className="text-sm text-yellow-800">
                                    <strong>Warning:</strong> Changing the secret key will reset the setup and you need to run connection test and ingestion again with the updated credentials.
                                </p>
                            </div>
                        )}
                        {errors.secretKey && <p className="text-red-600 text-sm mt-1">{errors.secretKey}</p>}
                    </div>
                </div>

                {/* Sync Start Date Section */}
                <div>
                    <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">2.4 Sync Start Date</h3>
                        {formData.syncStartDate && (
                            <CheckCircle className="w-5 h-5 text-green-600" />
                        )}
                    </div>
                    <div className="ml-7">
                        <p className="text-sm text-gray-600 mb-4">Choose the date from which you want to start syncing historical payment data.</p>
                        <div className="flex items-center gap-2 flex-wrap">
                            <div className="relative max-w-md flex-1 min-w-[200px]">
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (!isBasicConfigFieldsLocked || isEditingSyncDate || !sourceConfigurations.stripe?.syncStartDate) {
                                            setShowCalendar(!showCalendar);
                                        }
                                    }}
                                    disabled={isBasicConfigFieldsLocked && !!sourceConfigurations.stripe?.syncStartDate && !isEditingSyncDate}
                                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-left flex items-center justify-between ${
                                        isBasicConfigFieldsLocked && !isEditingSyncDate && !!sourceConfigurations.stripe?.syncStartDate
                                            ? 'bg-gray-50 cursor-not-allowed'
                                            : ''
                                    }`}
                                >
                                    <span className={formData.syncStartDate ? 'text-gray-900' : 'text-gray-500'}>
                                        {formData.syncStartDate instanceof Date ? formatDate(formData.syncStartDate) : 'Pick a date'}
                                    </span>
                                    <CalendarIcon className="h-4 w-4 text-gray-400" />
                                </button>
                                {showCalendar && renderCalendar()}
                            </div>
                            {isBasicConfigFieldsLocked && sourceConfigurations.stripe?.syncStartDate && !isEditingSyncDate && (
                                <button
                                    type="button"
                                    onClick={handleEditSyncDate}
                                    className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md transition-colors"
                                    title="Edit"
                                >
                                    <Pencil className="w-4 h-4" />
                                </button>
                            )}
                            {isEditingSyncDate && (
                                <div className="flex gap-2">
                                    <button
                                        type="button"
                                        onClick={handleSaveSyncDate}
                                        className="px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors whitespace-nowrap"
                                    >
                                        Save
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleCancelSyncDate}
                                        className="px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors whitespace-nowrap"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            )}
                        </div>
                        {isEditingSyncDate && (
                            <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                                <p className="text-sm text-yellow-800">
                                    <strong>Warning:</strong> Changing the sync start date will reset the setup and you need to run connection test and ingestion again with the updated settings.
                                </p>
                            </div>
                        )}
                        {errors.syncStartDate && <p className="text-red-600 text-sm mt-1">{errors.syncStartDate.toString()}</p>}
                    </div>
                </div>

                {/* Save (Step 2) */}
                <div className="flex justify-end pt-4 border-t border-gray-200">
                    {(() => {
                        const isBigSaveDisabled =
                            !isStep2Complete ||
                            isEditingSourceName ||
                            isEditingSecretKey ||
                            isEditingSyncDate;

                        return (
                            <button
                                type="button"
                                onClick={() => {
                                    setBasicConfigSavedThisSession(true);
                                    setIsSetupIncomplete(false);
                                    
                                    // Set manual step change BEFORE any config updates to prevent auto-navigation
                                    setIsManualStepChange(true);
                                    userChosenStepRef.current = 'revenue-correlation';

                                    if (pendingSecretKeyChange || pendingSyncDateChange) {
                                        const mode: PostSaveFlowMode =
                                            pendingSecretKeyChange && pendingSyncDateChange
                                                ? 'both'
                                                : pendingSyncDateChange
                                                  ? 'syncDateOnly'
                                                  : pendingSecretKeyChange
                                                    ? 'apiKeyOnly'
                                                    : 'none';

                                        setPostSaveFlowMode(mode);
                                        
                                        const updatedConfig = {
                                            ...formData,
                                            pendingSecretKeyChange: pendingSecretKeyChange,
                                            pendingSyncDateChange: pendingSyncDateChange,
                                            step1Completed: !!(formData.environmentId && formData.stripeEnvironment),
                                            step2Completed: !!(formData.sourceName && formData.accountId && formData.secretKey && formData.syncStartDate)
                                        };
                                        updateSourceConfiguration('stripe', updatedConfig);
                                        setConfig(updatedConfig);
                                    } else {
                                        // First time save - use the standard save function
                                        saveCurrentStepConfiguration();
                                    }
                                    
                                    removeStripeFormData();
                                    onAutoSave();

                                    // Navigate to Step 3
                                    setActiveStep('revenue-correlation');
                                    setTimeout(() => {
                                        step3ContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                    }, 100);
                                }}
                                disabled={isBigSaveDisabled}
                                className={`px-6 py-2 rounded-md font-medium transition-colors ${
                                    isBigSaveDisabled
                                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                        : 'bg-blue-600 text-white hover:bg-blue-700'
                                }`}
                            >
                                Save
                            </button>
                        );
                    })()}
                </div>
                <DemoStripeModal
                    isOpen={showDemoModal}
                    onClose={() => setShowDemoModal(false)}
                    onConnect={handleDemoStripeConnect}
                />
            </div>
        );
    };

    const handleTestSync = async (scenario: keyof typeof DEMO_TEST_SCENARIOS = 'SUCCESS') => {
        setIsTesting(true);
        setTestCompleted(false);
        setSelectedOption(null);
        setIsTablesExpanded(false);
        setIsStep3Incomplete(false);
        setHasNoCustomerData(false);
        setMissingEntitiesAcknowledged(false);
        setIsEntityErrorExpanded(true);

        const initialSteps: TestingStep[] = testingSteps.map(step => ({ ...step, status: 'pending' as const, missingEntities: [] }));
        setTestingSteps(initialSteps);

        let finalSteps: TestingStep[] = initialSteps.map(s => ({ ...s }));

        try {
            // Step 1: Connection test
            setTestingSteps(prev => prev.map((step, index) =>
                index === 0 ? { ...step, status: 'loading' as const } : step
            ));
            finalSteps[0] = { ...finalSteps[0], status: 'loading' as const };
            await new Promise(resolve => setTimeout(resolve, 500));

            const apiResult = await testIntegration(scenario);

            setTestingSteps(prev => prev.map((step, index) =>
                index === 0 ? { ...step, status: 'success' as const } : step
            ));
            finalSteps[0] = { ...finalSteps[0], status: 'success' as const };
            await new Promise(resolve => setTimeout(resolve, 800));

            // Step 2: Data retrieval test
            setTestingSteps(prev => prev.map((step, index) =>
                index === 1 ? { ...step, status: 'loading' as const } : step
            ));
            finalSteps[1] = { ...finalSteps[1], status: 'loading' as const };
            setTimeout(() => setIsTablesExpanded(true), 300);
            await new Promise(resolve => setTimeout(resolve, 1000));

            const hasMissingEntities = apiResult?.missingEntities && apiResult.missingEntities.length > 0;
            const customersList = apiResult?.customers ?? [];
            const customersInaccessible = apiResult?.missingEntities?.includes('customers');
            const noCustomerData = customersInaccessible || customersList.length === 0;

            setTestingSteps(prev => prev.map((step, index) =>
                index === 1 ? {
                    ...step,
                    status: noCustomerData || hasMissingEntities ? 'error' as const : 'success' as const,
                    missingEntities: apiResult?.missingEntities || []
                } : step
            ));
            finalSteps[1] = {
                ...finalSteps[1],
                status: noCustomerData || hasMissingEntities ? 'error' as const : 'success' as const,
                missingEntities: apiResult?.missingEntities || []
            };

            if (noCustomerData) {
                setHasNoCustomerData(true);
                setTimeout(() => setIsTablesExpanded(false), 1200);
                setIsTesting(false);
                setTestCompleted(true);
                saveConfiguration(true, { testingSteps: finalSteps, testCompleted: true });
                return;
            }

            setTimeout(() => setIsTablesExpanded(false), 1200);
            await new Promise(resolve => setTimeout(resolve, 800));

            setTestingSteps(prev => prev.map((step, index) =>
                index === 2 ? { ...step, status: 'loading' as const } : step
            ));
            finalSteps[2] = { ...finalSteps[2], status: 'loading' as const };
            await new Promise(resolve => setTimeout(resolve, 500));

            // Use fresh result from API (not stale state) to determine schema status
            const freshMetadataKeys = apiResult?.customerMetaDataKeys ?? [];
            const hasMetadataKeys = freshMetadataKeys.length > 0;
            const freshSelectedIdentifierKey = resolveSelectedAccountKey(freshMetadataKeys, selectedAccountKey);
            const freshGroupIdFound = !!findGroupIdKey(freshMetadataKeys);

            let schemaStatus: 'success' | 'error';

            if (!hasMetadataKeys) {
                schemaStatus = 'error';
            } else {
                schemaStatus = 'success';
            }

            setTestingSteps(prev => prev.map((step, index) =>
                index === 2 ? {
                    ...step,
                    status: schemaStatus,
                    selectedIdentifierKey: freshSelectedIdentifierKey
                } : step
            ));
            finalSteps[2] = {
                ...finalSteps[2],
                status: schemaStatus,
                selectedIdentifierKey: freshSelectedIdentifierKey,
                customerMetaDataKeys: freshMetadataKeys
            };

            await new Promise(resolve => setTimeout(resolve, 800));

            if (freshMetadataKeys.length > 0) {
                setSelectedAccountKey(freshSelectedIdentifierKey);
            }
            setGroupIdFound(freshGroupIdFound);
            setNoGroupIdWarningDismissed(freshGroupIdFound);
            setStep3Completed(true);

            const step3Incomplete =
                hasMissingEntities ||
                !freshGroupIdFound ||
                (step4WasEverCompleted ? isStep3Incomplete : false);

            if (!step4WasEverCompleted) {
                setIsStep3Incomplete(hasMissingEntities || !freshGroupIdFound);
            } else if (hasMissingEntities) {
                setIsStep3Incomplete(true);
            }

            saveConfiguration(step4WasEverCompleted ? step3Incomplete : (hasMissingEntities || !freshGroupIdFound), {
                testingSteps: finalSteps,
                testCompleted: true,
                selectedIdentifierKey: freshSelectedIdentifierKey,
                customerMetaDataKeys: freshMetadataKeys,
                groupIdFound: freshGroupIdFound
            });

        } catch (error) {
            console.error('Test failed:', error);
            finalSteps = finalSteps.map(step =>
                step.status === 'loading' ? { ...step, status: 'error' as const } : step
            );
            setTestingSteps(prev => prev.map(step =>
                step.status === 'loading' ? { ...step, status: 'error' as const } : step
            ));
            setTestCompleted(true);
            saveConfiguration(true, { testingSteps: finalSteps, testCompleted: true });
        }

        setIsTesting(false);
        setTestCompleted(true);
    };

    const renderTestingSteps = () => {
        const dataStep = testingSteps.find(s => s.id === 'data');
        const isDataStepUnresolved =
            dataStep?.status === 'pending' || dataStep?.status === 'loading';
        const hasEntityMissingError =
            dataStep?.status === 'error' &&
            !hasNoCustomerData &&
            (dataStep.missingEntities?.length ?? 0) > 0;
        const showSchemaStepUi =
            !hasNoCustomerData &&
            (missingEntitiesAcknowledged ||
                (!hasEntityMissingError && !(isTesting && isDataStepUnresolved)));

        return (
            <div className="mt-4 space-y-3 border border-gray-200 p-4 rounded-lg">
                {testingSteps.map((step) => {
                    if (step.id === 'schema' && !showSchemaStepUi) {
                        return null;
                    }
                    const isDemoSchemaStep = isDemoMode && step.id === 'schema' && step.status === 'success';
                    const displayedMetaDataKeys = isDemoSchemaStep
                        ? DEMO_SCHEMA_SCENARIO_METADATA_KEYS[demoSchemaScenario]
                        : customerMetaDataKeys;
                    const displayedSelectedAccountKey = resolveSelectedAccountKey(displayedMetaDataKeys, selectedAccountKey);
                    const displayedGroupIdFound = isDemoSchemaStep
                        ? !!findGroupIdKey(displayedMetaDataKeys)
                        : groupIdFound;
                    const isNoGroupIdScenario = !displayedGroupIdFound && displayedMetaDataKeys.length > 0;
                    const showNoGroupIdDropdownWarning = isNoGroupIdScenario && !noGroupIdWarningDismissed;

                    return (
                    <div key={step.id} className="flex items-start space-x-3">
                        <div className="flex-shrink-0 mt-1 text-lg">
                            {step.status === 'loading' && (
                                <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
                            )}
                            {step.status === 'success' && (
                                <CheckCircle className="w-4 h-4 text-green-600" />
                            )}
                            {step.status === 'error' && (
                                <AlertTriangle className="w-4 h-4 text-red-600" />
                            )}
                            {step.status === 'pending' && (
                                <div className="w-4 h-4 rounded-full border-2 border-gray-300"></div>
                            )}
                        </div>

                        <div className="flex-1">
                            <div className="font-medium text-gray-900 text-lg flex items-center justify-between gap-3 flex-wrap">
                                <span>
                                    {step.status === 'loading' && step.loadingText}
                                    {step.status === 'success' && step.successText}
                                    {step.status === 'error' && (step.id === 'data' ? 'Failed to Retrieve Data' : step.errorText)}
                                    {step.status === 'pending' && step.loadingText}
                                </span>

                                {isDemoSchemaStep && (
                                    <div className="flex items-center gap-2">
                                        {DEMO_SCHEMA_SCENARIO_OPTIONS.map((option) => (
                                            <button
                                                key={option.id}
                                                type="button"
                                                onClick={() => {
                                                    setDemoSchemaScenario(option.id);
                                                    setNoGroupIdWarningDismissed(option.id !== 'no_group_id');
                                                    setShowAccountKeyDropdown(false);
                                                }}
                                                className={`px-2 py-1 text-xs rounded-md border transition-colors ${demoSchemaScenario === option.id
                                                    ? 'bg-blue-600 text-white border-blue-600'
                                                    : 'bg-white text-blue-600 border-blue-600 hover:bg-blue-50'
                                                    }`}
                                            >
                                                {option.label}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Show tables during Test */}
                            {step.id === 'data' && step.status !== 'pending' && (
                                <div className="mt-2">
                                    <button
                                        type="button"
                                        onClick={() => setIsTablesExpanded(!isTablesExpanded)}
                                        className="flex items-center space-x-2 text-blue-600 hover:text-blue-800 text-sm font-medium mb-2"
                                    >
                                        <ChevronDown className={`w-4 h-4 transition-transform ${isTablesExpanded ? '' : '-rotate-90'}`} />
                                        <span>List of Entities Syncing</span>
                                    </button>

                                    {isTablesExpanded && (
                                        <div className="border-l-2 border-gray-200 pl-4 ml-6 transition-all duration-500 ease-in-out transform">
                                            <div className="space-y-2">
                                                <div className="text-sm text-gray-600">
                                                    Total tables in Stripe: <span className="font-medium text-gray-900">{STRIPE_TABLES.available.length}</span>
                                                </div>
                                                <div className="text-sm text-gray-600">
                                                    Tables to be synced to TS: <span className="font-medium text-gray-900">{STRIPE_TABLES.willSync.length}</span>
                                                    <ul className="flex flex-col ml-1 mt-2 space-y-1">
                                                        {STRIPE_TABLES.willSync.map(entity => {
                                                            const isMissing = step.missingEntities?.includes(entity);
                                                            return (
                                                                <li key={entity} className="flex items-center space-x-2 text-sm">
                                                                    {isMissing ? (
                                                                        <X className="w-4 h-4 text-red-600 flex-shrink-0" />
                                                                    ) : (
                                                                        <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                                                                    )}
                                                                    <span className={isMissing ? 'text-red-700 font-medium' : 'text-gray-700'}>
                                                                        {entity}
                                                                    </span>
                                                                </li>
                                                            );
                                                        })}
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* No customer data — hard block */}
                            {step.id === 'data' && step.status === 'error' && hasNoCustomerData && (
                                <div className="space-y-4 mt-3">
                                    <div className="p-4 bg-red-50 rounded-md">
                                        <div className="space-y-3">
                                            <h4 className="font-semibold text-gray-900 text-base">
                                                No customer data found — <span className="font-normal">No customers was found for this secret key.</span>
                                            </h4>
                                            <div className="flex items-center gap-3 flex-wrap">
                                                <button
                                                    type="button"
                                                    className="px-4 py-2 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-md font-medium text-sm transition-colors"
                                                    onClick={() => handleTestSync('SUCCESS')}
                                                >
                                                    Retry Test
                                                </button>
                                                <button
                                                    type="button"
                                                    className="px-4 py-2 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-md font-medium text-sm transition-colors"
                                                    onClick={handleNavigateToSecretKey}
                                                >
                                                    Change Credentials
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Missing entities warning — proceed allowed when customers exist */}
                            {step.id === 'data' && step.status === 'error' && !hasNoCustomerData && step.missingEntities && step.missingEntities.length > 0 && (
                                <div className="space-y-4 mt-3">
                                    <div className="p-4 bg-red-50 rounded-md">
                                        {missingEntitiesAcknowledged && (
                                            <button
                                                type="button"
                                                onClick={() => setIsEntityErrorExpanded(!isEntityErrorExpanded)}
                                                className="w-full flex items-start gap-2 text-left"
                                            >
                                                <ChevronDown className={`w-4 h-4 text-red-600 flex-shrink-0 mt-1 transition-transform ${isEntityErrorExpanded ? '' : '-rotate-90'}`} />
                                                <h4 className="font-semibold text-gray-900 text-base">
                                                    Unable to retrieve some entities — <span className="font-normal">Your secret key doesn&apos;t have access to {step.missingEntities?.length || 0} / {STRIPE_TABLES.willSync.length} required Stripe {step.missingEntities?.length === 1 ? 'table' : 'tables'}.</span>
                                                </h4>
                                            </button>
                                        )}
                                        {(!missingEntitiesAcknowledged || isEntityErrorExpanded) && (
                                        <div className={`space-y-3 ${missingEntitiesAcknowledged ? 'mt-3 pl-6' : ''}`}>
                                            {!missingEntitiesAcknowledged && (
                                            <h4 className="font-semibold text-gray-900 text-base">
                                                Unable to retrieve some entities — <span className="font-normal">Your secret key doesn't have access to {step.missingEntities?.length || 0} / {STRIPE_TABLES.willSync.length} required Stripe {step.missingEntities?.length === 1 ? 'table' : 'tables'}.</span>
                                            </h4>
                                            )}
                                            <h4 className="font-semibold text-gray-900 text-base">
                                                To resolve this, you need to{' '}
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setIsSetupGuideOpen(true);
                                                        setTimeout(() => {
                                                            const setupSection = document.querySelector('[data-section="setup-stripe"]');
                                                            if (setupSection) {
                                                                setupSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                                            }
                                                        }, 100);
                                                    }}
                                                    className="text-blue-600 hover:text-blue-800 underline font-medium"
                                                >
                                                    configure your secret key's permissions
                                                </button>
                                                .
                                            </h4>

                                            <div className="bg-white p-3 rounded space-y-4">
                                                <div className="text-sm font-medium text-gray-900">Missing entities and revenue impact:</div>
                                                <ul className="space-y-4">
                                                    {sortMissingEntitiesForDisplay(step.missingEntities).map(entity => {
                                                        const impact = getStripeEntityImpact(entity);
                                                        const isSubscriptionsEntity = entity === 'subscriptions';
                                                        return (
                                                            <li key={entity} className="border-b border-gray-100 pb-3 last:border-b-0 last:pb-0">
                                                                <div className="flex items-start gap-2">
                                                                    <X className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                                                                    <div className="space-y-1.5 flex-1 min-w-0">
                                                                        <span className="text-base font-semibold text-red-700">{impact?.label ?? entity}</span>
                                                                        {impact?.affectedReports && impact.affectedReports.length > 0 && (
                                                                            <p className="text-xs text-gray-600">
                                                                                <span className="font-medium text-gray-700">Affected Metrics: </span>
                                                                                {impact.affectedReports.join(', ')}
                                                                            </p>
                                                                        )}
                                                                        {impact?.explanation && (
                                                                            <p className="text-sm text-gray-700 leading-relaxed">{impact.explanation}</p>
                                                                        )}
                                                                        {isSubscriptionsEntity && (
                                                                            <div className="mt-3 bg-amber-50 text-amber-700 rounded-xl px-5 py-4">
                                                                                <div className="flex items-start gap-2">
                                                                                    <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                                                                    <div className="text-sm space-y-2">
                                                                                        <p>If you are not using Stripe Billing for subscriptions or used only as a payment gateway, switch to a transaction-based connection:</p>
                                                                                        <ol className="list-decimal pl-5 space-y-1.5">
                                                                                            <li>
                                                                                                <a
                                                                                                    href="#"
                                                                                                    onClick={(e) => {
                                                                                                        e.preventDefault();
                                                                                                        onOpenIntegrationsList();
                                                                                                    }}
                                                                                                    className="font-semibold underline"
                                                                                                >
                                                                                                    Go to Integrations
                                                                                                </a>
                                                                                                {' '}and delete this Stripe connection.
                                                                                            </li>
                                                                                            <li>
                                                                                                In <span className="font-medium">Data Source</span> (Step 1 of revenue setup), choose <span className="font-medium">Transactions &amp; Invoices</span>.
                                                                                            </li>
                                                                                            <li>Choose <span className="font-medium">Stripe</span> and set up the connection again.</li>
                                                                                        </ol>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </li>
                                                        );
                                                    })}
                                                </ul>
                                            </div>
                                            <div className="flex items-center gap-3 flex-wrap">
                                                <button
                                                    type="button"
                                                    className="px-4 py-2 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-md font-medium text-sm transition-colors"
                                                    onClick={() => handleTestSync('SUCCESS')}
                                                >
                                                    Retry Test
                                                </button>
                                                <button
                                                    type="button"
                                                    className="px-4 py-2 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-md font-medium text-sm transition-colors"
                                                    onClick={handleNavigateToSecretKey}
                                                >
                                                    Change Credentials
                                                </button>
                                                {!missingEntitiesAcknowledged && (
                                                <button
                                                    type="button"
                                                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium text-sm transition-colors"
                                                    onClick={handleProceedDespiteMissingEntities}
                                                >
                                                    Proceed Anyway
                                                </button>
                                                )}
                                            </div>
                                        </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Schema Error Info - ONLY show when NO metadata keys */}
                            {step.id === 'schema' &&
                                step.status !== 'pending' &&
                                (!displayedMetaDataKeys || displayedMetaDataKeys.length === 0) && (
                                    <div className="space-y-3 mt-3">
                                        <p className="text-sm text-gray-700">
                                            Account identifier key is missing in Stripe customer metadata. Fix it to enable correlation across product, marketing and revenue data.
                                        </p>
                                        <FixInstructionsContent source="stripe" accountIdentifierKey={DEFAULT_ACCOUNT_IDENTIFIER_KEY} />
                                        <div className="flex items-center gap-3">
                                            <button
                                                type="button"
                                                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium text-sm transition-colors inline-flex items-center gap-2"
                                                onClick={async () => {
                                                    if (pendingSecretKeyChange || pendingSyncDateChange) {
                                                        setIsUpdatingSource(true);
                                                        setUpdateSourceError(null);
                                                        
                                                        try {
                                                            await updateAirbyteSource({
                                                                variables: {
                                                                    input: {
                                                                        environmentId: formData.environmentId,
                                                                        sourceType: IntegrationType.Stripe,
                                                                        startDate:
                                                                            formData.syncStartDate instanceof Date
                                                                                ? formData.syncStartDate.toISOString().split('.')[0] + 'Z'
                                                                                : new Date().toISOString().split('.')[0] + 'Z',
                                                                        apiKey: formData.secretKey,
                                                                        shouldReset: false
                                                                    }
                                                                }
                                                            });
                                                            
                                                            
                                                        } catch (e) {
                                                            console.error('Failed to update Stripe source:', e);
                                                            setUpdateSourceError('Something went wrong, try again');
                                                            setIsUpdatingSource(false);
                                                            return;
                                                        }
                                                        
                                                        setIsUpdatingSource(false);
                                                    }
                                                    
                                                    setSelectedTeam(TeamId.REVOPS_TEAMS);
                                                    setShowInviteModal(true);
                                                    setIsStep3Incomplete(true);
                                                    setStep3Completed(true);
                                                    saveConfiguration(true, {
                                                        selectedIdentifierKey: DEFAULT_ACCOUNT_IDENTIFIER_KEY,
                                                        groupIdMapping: true
                                                    });
                                                    userChosenStepRef.current = 'pause-fix';
                                                    setActiveStep('pause-fix');
                                                    onAutoSave();
                                                }}
                                                disabled={isUpdatingSource}
                                            >
                                                {isUpdatingSource && <Loader2 className="w-4 h-4 animate-spin" />}
                                                {isUpdatingSource ? 'Saving...' : updateSourceError ? 'Retry Invite and Proceed' : 'Invite and Proceed'}
                                            </button>
                                            <button
                                                type="button"
                                                className="px-4 py-2 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-md font-medium text-sm transition-colors"
                                                onClick={handleProceedAnywayWithoutInvite}
                                                disabled={isUpdatingSource}
                                            >
                                                Proceed Anyway
                                            </button>
                                        </div>
                                        {updateSourceError && (
                                            <span className="text-red-600 text-sm font-medium ml-3">{updateSourceError}</span>
                                        )}
                                    </div>
                                )}

                            {/* Account Identifier Dropdown - Show ONLY when metadata keys exist (regardless of step status) */}
                            {step.id === 'schema' &&
                                displayedMetaDataKeys &&
                                displayedMetaDataKeys.length > 0 && (
                                    <>
                                    <div className="space-y-4 mt-3">
                                        <div className="p-4 bg-gray-50 rounded-lg">
                                            <div className="space-y-3">
                                                <div>
                                                    <p className="text-sm text-gray-900 mb-3">
                                                        To uniquely identify the account information and to correlate it with revenue data, we look at the following key. This key enables seamless correlation across revenue, marketing, and product data.
                                                    </p>
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                                        Account Identifier Key
                                                    </label>
                                                    <div className="relative">
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                const nextOpenState = !showAccountKeyDropdown;
                                                                setShowAccountKeyDropdown(nextOpenState);
                                                                if (nextOpenState && isNoGroupIdScenario) {
                                                                    setNoGroupIdWarningDismissed(true);
                                                                }
                                                            }}
                                                            className="w-64 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-left flex items-center justify-between"
                                                        >
                                                            <span className="flex items-center gap-1 text-gray-900 font-medium">
                                                                {showNoGroupIdDropdownWarning && (
                                                                    <AlertTriangle className="w-4 h-4 text-amber-600" />
                                                                )}
                                                                <span>{displayedSelectedAccountKey}</span>
                                                            </span>
                                                            <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showAccountKeyDropdown ? 'rotate-180' : ''}`} />
                                                        </button>

                                                        {showAccountKeyDropdown && (
                                                            <div className="absolute z-10 w-64 mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                                                                <div className="py-1">
                                                                    {displayedMetaDataKeys.map((key) => (
                                                                        <button
                                                                            key={key}
                                                                            type="button"
                                                                            onClick={() => {
                                                                                setSelectedAccountKey(key);
                                                                                setShowAccountKeyDropdown(false);
                                                                                setTestingSteps(prev => prev.map(step =>
                                                                                    step.id === 'schema'
                                                                                        ? {
                                                                                            ...step,
                                                                                            selectedIdentifierKey: key,
                                                                                            customerMetaDataKeys: displayedMetaDataKeys
                                                                                        }
                                                                                        : step
                                                                                ));
                                                                            }}
                                                                            className="w-full px-3 py-2 text-left text-sm hover:bg-blue-50 hover:text-blue-900 flex items-center justify-between"
                                                                        >
                                                                            <span>{key}</span>
                                                                            {displayedSelectedAccountKey === key && (
                                                                                <Check className="w-4 h-4 text-blue-600" />
                                                                            )}
                                                                        </button>
                                                                    ))}
                                                                </div>
                                                        </div>
                                                    )}

                                                    {displayedGroupIdFound && (
                                                        <>
                                                            <p className="flex items-center gap-x-1 text-sm bg-amber-50 text-amber-700 rounded-xl px-5 py-4 mb-3 mt-2">
                                                                <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                                                                <span>
                                                                    Group ID is set as the default account identifier. Please select an alternative key from the dropdown menu if this is incorrect.
                                                                </span>
                                                            </p>
                                                        </>
                                                    )}

                                                    {!displayedGroupIdFound && (
                                                        <div className="mt-3">
                                                            <div className="bg-amber-50 text-amber-700 rounded-xl px-5 py-4">
                                                                <div className="flex items-start gap-2">
                                                                    <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                                                    <div className="text-sm">
                                                                        <p className="mb-1">group_id was not found in the customer metadata from Stripe.</p>
                                                                        <ul className="pl-5 space-y-2">
                                                                            <li className="list-disc">
                                                                                <span className="font-medium">Option 1:</span> Select the correct account identifier from the dropdown above.
                                                                            </li>
                                                                            <li className="list-none text-center text-xs font-semibold uppercase tracking-wide text-amber-700">
                                                                                OR
                                                                            </li>
                                                                            <li className="list-disc">
                                                                                <span className="font-medium">Option 2:</span> Resolve this by using the AI-assisted or manual steps below to add the missing key in customer metadata of Stripe.
                                                                            </li>
                                                                        </ul>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                    <p className="flex items-center text-sm text-gray-700 mx-2 mb-2">
                                                        If your product telemetry's account identifier key is missing from the dropdown, use the tabs below to resolve this.
                                                        <button
                                                            type="button"
                                                            onClick={() => setShowFixInstructionsModal(true)}
                                                            className="text-blue-600 underline hover:text-blue-800 ml-1"
                                                        >
                                                            See the guide
                                                        </button>
                                                        .
                                                    </p>
                                                    <FixInstructionsContent source="stripe" accountIdentifierKey={DEFAULT_ACCOUNT_IDENTIFIER_KEY} />
                                                    </div>
                                                </div>

                                                {/* BOTH BUTTONS ALWAYS SHOWN */}
                                                <div className="flex items-center gap-3 pt-2">
                                                    <button
                                                        type="button"
                                                        onClick={handleInviteAndProceedWithMetadata}
                                                        disabled={isUpdatingSource}
                                                        className={`px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium text-sm transition-colors inline-flex items-center gap-2 ${
                                                            isUpdatingSource ? 'opacity-70 cursor-not-allowed' : ''
                                                        }`}
                                                    >
                                                        {isUpdatingSource && <Loader2 className="w-4 h-4 animate-spin" />}
                                                        {isUpdatingSource ? 'Saving...' : updateSourceError ? 'Retry Invite and Proceed' : 'Invite and Proceed'}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={handleSaveAndProceed}
                                                        disabled={isUpdatingSource}
                                                        className={`px-4 py-2 bg-white border border-blue-600 hover:bg-blue-50 text-blue-600 rounded-md font-medium text-sm transition-colors inline-flex items-center gap-2 ${
                                                            isUpdatingSource ? 'opacity-70 cursor-not-allowed' : ''
                                                        }`}
                                                    >
                                                        {isUpdatingSource && <Loader2 className="w-4 h-4 animate-spin" />}
                                                        {isUpdatingSource ? 'Saving...' : updateSourceError ? 'Retry Save and Proceed' : 'Save and Proceed'}
                                                    </button>
                                                    {updateSourceError && (
                                                        <span className="text-red-600 text-sm font-medium">{updateSourceError}</span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    </>
                                )}
                        </div>
                    </div>
                )})}
            </div>
        );
    };

    const renderRevenueCorrelationContent = () => {
        return (
            <div className="space-y-6">
                <div>
                    <p className="text-gray-700 leading-relaxed text-sm">
                        We will use the credentials from <span className="font-medium text-gray-900">Basic Configuration</span> (step 2)
                        to test the connection with Stripe and sync sample data of 200 Transactions.
                    </p>

                    <div className="flex items-center gap-3 mt-2 flex-wrap">
                        <button
                            type="button"
                            className={`px-4 py-2 rounded-md font-medium transition-all duration-200 flex items-center space-x-2 min-w-[120px] justify-center ${isTesting
                                ? 'bg-blue-600 text-white cursor-default'
                                : 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white shadow-sm hover:shadow-md'
                                }`}
                            onClick={() => handleTestSync('SUCCESS')}
                            disabled={isTesting}
                        >
                            {isTesting ? (
                                <>
                                    <Database className="w-4 h-4" />
                                    <span>Testing{'.'.repeat(dotCount)}</span>
                                </>
                            ) : testCompleted ? (
                                <>
                                    <Database className="w-4 h-4" />
                                    <span>Retry</span>
                                </>
                            ) : (
                                <>
                                    <Database className="w-4 h-4" />
                                    <span>Test Data Sync</span>
                                </>
                            )}
                        </button>

                        {isDemoMode && !isTesting && (
                            <>
                                <button
                                    type="button"
                                    className="px-4 py-2 rounded-md font-medium transition-all duration-200 flex items-center space-x-2 border border-blue-600 bg-white text-blue-600 hover:bg-blue-50"
                                    onClick={() => handleTestSync('MISSING_ENTITIES')}
                                >
                                    <Database className="w-4 h-4" />
                                    <span>Simulate Missing Entities</span>
                                </button>

                                <button
                                    type="button"
                                    className="px-4 py-2 rounded-md font-medium transition-all duration-200 flex items-center space-x-2 border border-blue-600 bg-white text-blue-600 hover:bg-blue-50"
                                    onClick={() => handleTestSync('MISSING_GROUP_ID')}
                                >
                                    <Database className="w-4 h-4" />
                                    <span>Simulate Schema Error</span>
                                </button>
                            </>
                        )}

                        {testCompleted && areAllStepsSuccessful() && (
                            <>
                                <div className="flex items-center space-x-3">
                                    <div className="flex items-center space-x-2 text-green-600">
                                        <CheckCircle className="w-4 h-4" />
                                        <span className="text-sm font-medium">Test Completed - Schema Verified</span>
                                    </div>
                                </div>
                            </>
                        )}

                    </div>

                    {/* Testing Steps */}
                    {renderTestingSteps()}

                    {/* Updated: Only show this button when test completes successfully AND no metadata scenarios */}
                    {testCompleted &&
                        areAllStepsSuccessful() &&
                        (!customerMetaDataKeys || customerMetaDataKeys.length === 0) && (
                            <button
                                type="button"
                                onClick={() => {
                                    saveConfiguration(false);
                                    userChosenStepRef.current = 'pause-fix';
                                    setActiveStep('pause-fix');
                                    setStep3Completed(true);
                                }}
                                className="flex px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium mt-4 ml-auto"
                            >
                                Proceed to Next Step
                            </button>
                        )}
                </div>
            </div>
        );
    };

    const renderIngestionLoadingSteps = () => {
        if (!showIngestionLoadingSteps) return null;
        return (
            <div className="mt-4 space-y-3 border border-gray-200 p-4 rounded-lg">
                {ingestionLoadingSteps.map((step, idx) => (
                    <div key={step.id} className="flex items-start space-x-3">
                        <div className="flex-shrink-0 mt-1 text-lg">
                            {step.status === 'loading' && <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />}
                            {step.status === 'success' && <CheckCircle className="w-4 h-4 text-green-600" />}
                            {step.status === 'error' && <AlertTriangle className="w-4 h-4 text-red-600" />}
                            {step.status === 'pending' && (
                                <div className="w-4 h-4 rounded-full border-2 border-gray-300" />
                            )}
                        </div>
                        <div className="flex-1">
                            <div className="font-medium text-gray-900 text-lg">
                                {step.status === 'loading' && step.loadingText}
                                {step.status === 'success' && step.successText}
                                {step.status === 'error' && step.errorText}
                                {step.status === 'pending' && step.loadingText}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    };


    const renderPauseFixContent = () => {
        const isResyncFlow =
            (effectiveFlowMode === 'apiKeyOnly' ||
            effectiveFlowMode === 'both' ||
            effectiveFlowMode === 'syncDateOnly') &&
            step4WasEverCompleted;
        
        // Show button when step4 is not completed, regardless of whether it was completed before
        const shouldShowButton = !step4Completed;

        const handleStartIngestion = async () => {
            try {
                setIngestionError(null);
                
                const currentMode: PostSaveFlowMode =
                    pendingSecretKeyChange && pendingSyncDateChange
                        ? 'both'
                        : pendingSyncDateChange
                          ? 'syncDateOnly'
                          : pendingSecretKeyChange
                            ? 'apiKeyOnly'
                            : 'none'; 
                
                const actualIsResyncFlow =
                    currentMode === 'apiKeyOnly' ||
                    currentMode === 'both' ||
                    currentMode === 'syncDateOnly';
                
                // Handle updateAirbyteSource for existing integrations with pending changes
                // This was moved from step 3 to prevent API failures when source doesn't exist
                if ((pendingSecretKeyChange || pendingSyncDateChange) && 
                    hasSuccessfulIngestion && actualIsResyncFlow) {
                    
                    setIsUpdatingSource(true);
                    setUpdateSourceError(null);
                    
                    try {
                        await updateAirbyteSource({
                            variables: {
                                input: {
                                    environmentId: formData.environmentId,
                                    sourceType: IntegrationType.Stripe,
                                    startDate:
                                        formData.syncStartDate instanceof Date
                                            ? formData.syncStartDate.toISOString().split('.')[0] + 'Z'
                                            : new Date().toISOString().split('.')[0] + 'Z',
                                    apiKey: formData.secretKey,
                                    shouldReset: false
                                }
                            }
                        });
                        
                    } catch (e) {
                        console.error('Failed to update Stripe source:', e);
                        setUpdateSourceError('Failed to update source configuration');
                        setIsUpdatingSource(false);
                        setIngestionError('Failed to update source configuration. Please try again.');
                        return; 
                    }
                    
                    setIsUpdatingSource(false);
                }
                
                if (!actualIsResyncFlow) {
                    setShowIngestionLoadingSteps(true);
                    setIngestionStepIndex(0);
                    setIngestionLoadingSteps(ingestionLoadingStepsDefault);
                    setIngestionResult(null);
                }

                let response;

                if (currentMode === 'apiKeyOnly') {
                    const res = await triggerSync({
                        variables: {
                            input: {
                                environmentId: formData.environmentId,
                                dataSource: DataSource.Stripe
                            }
                        }
                    });

                    if (res.data?.triggerSync?.isSuccess) {
                        const nowIso = new Date().toISOString();
                        setStep4Completed(true);
                        setSyncStatus('running');
                        setIsSyncPolling(true);
                        setIsSetupIncomplete(false);
                        setResyncStarted(true);
                        
                        // Preserve existing ingestionResult and update sync status
                        const preservedIngestionResult = sourceConfigurations.stripe?.ingestionResult || ingestionResult;
                        const updatedIngestionResult = preservedIngestionResult ? {
                            ...preservedIngestionResult,
                            syncStatus: 'running',
                            syncStartedAt: nowIso
                        } : null;
                        setIngestionResult(updatedIngestionResult);
                        
                        setPendingSecretKeyChange(false);
                        setPendingSyncDateChange(false);

                        // Once step2 is completed (saved in config), it should remain true even if formData is incomplete
                        const step2ShouldBeCompleted = sourceConfigurations.stripe?.step2Completed === true 
                            ? true 
                            : !!(formData.sourceName && formData.accountId && formData.secretKey && formData.syncStartDate);

                        const cfgUpdate = {
                            ...formData,
                            status: 'configured',
                            completed: true,
                            completedAt: nowIso,
                            step1Completed: !!(formData.environmentId && formData.stripeEnvironment),
                            step2Completed: step2ShouldBeCompleted,
                            step3Completed: step3Completed,
                            step3CompletedAt: sourceConfigurations.stripe?.step3CompletedAt || nowIso,
                            step4Completed: true,
                            step4WasEverCompleted: true,
                            step4CompletedAt: nowIso,
                            isStep3Incomplete: isStep3Incomplete,
                            testingSteps: testingSteps,
                            testCompleted: testCompleted,
                            schemaVerified: sourceConfigurations.stripe?.schemaVerified || false,
                            selectedIdentifierKey: selectedAccountKey,
                            customerMetaDataKeys: customerMetaDataKeys,
                            groupIdFound: groupIdFound,
                            ingestionResult: updatedIngestionResult,
                            isSetupIncomplete: false,
                            pendingSecretKeyChange: false,
                            pendingSyncDateChange: false
                        };
                        updateSourceConfiguration('stripe', cfgUpdate);
                        setConfig(cfgUpdate);
                        onAutoSave();
                    } else {
                        setIngestionError('Failed to trigger resync');
                    }
                    return;
                }

                if (currentMode === 'syncDateOnly' || currentMode === 'both') {
                    const res = await resetAndResyncAirbyteData({
                        variables: {
                            input: {
                                environmentId: formData.environmentId,
                                sourceType: IntegrationType.Stripe
                            }
                        }
                    });

                    if (res.data?.resetAndResyncAirbyteData?.isSuccess) {
                        const nowIso = new Date().toISOString();
                        setStep4Completed(true);
                        setSyncStatus('running');
                        setIsSyncPolling(true);
                        setIsSetupIncomplete(false);
                        setResyncStarted(true);
                        
                        // Preserve existing ingestionResult and update sync status
                        const preservedIngestionResult = sourceConfigurations.stripe?.ingestionResult || ingestionResult;
                        const updatedIngestionResult = preservedIngestionResult ? {
                            ...preservedIngestionResult,
                            syncStatus: 'running',
                            syncStartedAt: nowIso
                        } : null;
                        setIngestionResult(updatedIngestionResult);
                        
                        setPendingSecretKeyChange(false);
                        setPendingSyncDateChange(false);

                        // Once step2 is completed (saved in config), it should remain true even if formData is incomplete
                        const step2ShouldBeCompleted = sourceConfigurations.stripe?.step2Completed === true 
                            ? true 
                            : !!(formData.sourceName && formData.accountId && formData.secretKey && formData.syncStartDate);

                        const cfgUpdate = {
                            ...formData,
                            status: 'configured',
                            completed: true,
                            completedAt: nowIso,
                            step1Completed: !!(formData.environmentId && formData.stripeEnvironment),
                            step2Completed: step2ShouldBeCompleted,
                            step3Completed: step3Completed,
                            step3CompletedAt: sourceConfigurations.stripe?.step3CompletedAt || nowIso,
                            step4Completed: true,
                            step4WasEverCompleted: true,
                            step4CompletedAt: nowIso,
                            isStep3Incomplete: isStep3Incomplete,
                            testingSteps: testingSteps,
                            testCompleted: testCompleted,
                            schemaVerified: sourceConfigurations.stripe?.schemaVerified || false,
                            selectedIdentifierKey: selectedAccountKey,
                            customerMetaDataKeys: customerMetaDataKeys,
                            groupIdFound: groupIdFound,
                            ingestionResult: updatedIngestionResult,
                            isSetupIncomplete: false,
                            pendingSecretKeyChange: false,
                            pendingSyncDateChange: false
                        };
                        updateSourceConfiguration('stripe', cfgUpdate);
                        setConfig(cfgUpdate);
                        onAutoSave();
                    } else {
                        setIngestionError(res.data?.resetAndResyncAirbyteData?.message || 'Failed to reset and resync');
                    }
                    return;
                }

                if (isDemoMode) {
                    response = await simulateCreateStripeIntegration({
                        environmentId: formData.environmentId,
                        stripeEnvironmentType: formData.stripeEnvironment,
                        stripeAccountId: formData.accountId,
                        stripeSecretKey: formData.secretKey,
                        syncStartTime: formData.syncStartDate instanceof Date ? formData.syncStartDate.toISOString() : new Date().toISOString()
                    });
                } else {
                    const stripeEnvType = formData.stripeEnvironment === 'live'
                        ? StripeEnvironmentType.Live
                        : StripeEnvironmentType.Test;

                    const result = await createAirbyteIntegration({
                        variables: {
                            input: {
                                environmentId: formData.environmentId,
                                integrationType: IntegrationType.Stripe,
                                syncStartTime: formData.syncStartDate instanceof Date ? formData.syncStartDate.toISOString().split('.')[0] + 'Z' : new Date().toISOString().split('.')[0] + 'Z',
                                stripeConfig: {
                                    environmentType: stripeEnvType,
                                    accountId: formData.accountId || '',
                                    secretKey: formData.secretKey
                                }
                            }
                        }
                    });

                    if (result.data?.createAirbyteIntegration) {
                        response = result.data.createAirbyteIntegration;
                    }
                }

                if (response) {
                    // Once step2 is completed (saved in config), it should remain true even if formData is incomplete
                    const step2ShouldBeCompleted = sourceConfigurations.stripe?.step2Completed === true 
                        ? true 
                        : !!(formData.sourceName && formData.accountId && formData.secretKey && formData.syncStartDate);
                    
                    const config = {
                        ...formData,
                        status: 'configured',
                        completed: true,
                        completedAt: new Date().toISOString(),
                        step1Completed: !!(formData.environmentId && formData.stripeEnvironment),
                        step2Completed: step2ShouldBeCompleted,
                        step3Completed: step3Completed,
                        step3CompletedAt: step3Completed ? (sourceConfigurations.stripe?.step3CompletedAt || new Date().toISOString()) : new Date().toISOString(),
                        step4Completed: true,
                        step4WasEverCompleted: true,
                        step4CompletedAt: new Date().toISOString(),
                        isStep3Incomplete: isStep3Incomplete,
                        testingSteps: testingSteps,
                        testCompleted: testCompleted,
                        schemaVerified: sourceConfigurations.stripe?.schemaVerified || false,
                        selectedIdentifierKey: selectedAccountKey,
                        customerMetaDataKeys: customerMetaDataKeys,
                        groupIdFound: groupIdFound,
                        ingestionResult: {
                            sourceId: response.sourceId,
                            connectionId: response.connectionId,
                            syncStartedAt: response.syncStartedAt,
                            syncStatus: response.syncStatus,
                            jobId: response.jobId
                        },
                        isSetupIncomplete: false
                    };
                    updateSourceConfiguration('stripe', config);
                    setConfig(config);
                    setStep4Completed(true);
                    onTrackCompletion?.();

                    setIngestionResult({
                        sourceId: response.sourceId,
                        connectionId: response.connectionId,
                        syncStartedAt: response.syncStartedAt,
                        syncStatus: response.syncStatus,
                        jobId: response.jobId
                    });

                    setSyncStatus('running');
                    setIsSyncPolling(true);
                    setIsSetupIncomplete(false);

                    if (response.syncStartedAt) {
                        setLastSyncedDate(response.syncStartedAt);
                    }

                    onAutoSave();

                    return response.syncStatus;
                }
            } catch (error) {
                console.error('Failed to start Stripe ingestion:', error);
                setIngestionError(error instanceof Error ? error.message : 'Unknown error occurred');
            }
        };

        const showStatusPanel = (step4Completed && !!ingestionResult) || 
            (step4Completed && resyncStarted && (syncStatus === 'running' || syncStatus === 'pending')) ||
            (isResyncFlow && (triggerSyncLoading || resetAndResyncLoading));
        const showResyncAction = shouldShowButton && !triggerSyncLoading && !resetAndResyncLoading;

        return (
            <div className="space-y-6">
                {showStatusPanel && (
                    <div className={`rounded-lg p-4 ${syncStatus === 'succeeded' ? 'bg-green-50' :
                        syncStatus === 'failed' ? 'bg-red-50' :
                            'bg-blue-50'
                        }`}>
                        <div className="flex items-start space-x-3">
                            {syncStatus === 'running' && (
                                <Loader2 className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0 animate-spin" />
                            )}
                            {syncStatus === 'succeeded' && (
                                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                            )}
                            {syncStatus === 'failed' && (
                                <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                            )}
                            {syncStatus === 'pending' && (
                                <Loader2 className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0 animate-spin" />
                            )}
                            <div className="flex-1">
                                <h4 className={`font-semibold text-xl mb-1 ${syncStatus === 'succeeded' ? 'text-green-900' :
                                    syncStatus === 'failed' ? 'text-red-900' :
                                        'text-blue-900'
                                    }`}>
                                    {syncStatus === 'running' || syncStatus === 'pending'
                                        ? 'Processing Ingestion'
                                        : syncStatus === 'succeeded'
                                            ? 'Ingestion Completed Successfully'
                                            : syncStatus === 'failed'
                                                ? 'Ingestion Failed'
                                                : 'Processing Ingestion'}
                                </h4>
                                <p className={`text-sm mb-2 ${syncStatus === 'succeeded' ? 'text-green-800' :
                                    syncStatus === 'failed' ? 'text-red-800' :
                                        'text-blue-800'
                                    }`}>
                                    {syncStatus === 'running' || syncStatus === 'pending'
                                        ? "We're currently syncing your Stripe data. This process may take a 5-7 minutes. You'll receive an email notification once complete."
                                        : syncStatus === 'succeeded'
                                            ? 'Your Stripe data has been successfully synced and is ready for analysis.'
                                            : syncStatus === 'failed'
                                                ? 'There was an error syncing your Stripe data. Please try again or contact support.'
                                                : "We're currently syncing your Stripe data. This process may take a 5-7 minutes. You'll receive an email notification once complete."}
                                </p>
                                <div className={`text-xs ${syncStatus === 'succeeded' ? 'text-green-700' :
                                    syncStatus === 'failed' ? 'text-red-700' :
                                        'text-blue-700'
                                    }`}>
                                    {lastSyncedDate && (
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className="font-medium">Last synced:</span>
                                            <span>{new Date(lastSyncedDate).toLocaleString('en-US', {
                                                weekday: 'short',
                                                year: 'numeric',
                                                month: 'short',
                                                day: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit',
                                                second: '2-digit'
                                            })}</span>
                                            {isSyncPolling && (
                                                <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse" title="Checking status every 30 seconds"></div>
                                            )}
                                        </div>
                                    )}
                                    {!lastSyncedDate && ingestionResult?.syncStartedAt && (
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className="font-medium">Sync started at:</span>
                                            <span>{new Date(ingestionResult.syncStartedAt).toLocaleString('en-US', {
                                                weekday: 'short',
                                                year: 'numeric',
                                                month: 'short',
                                                day: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit',
                                                second: '2-digit'
                                            })}</span>
                                            {isSyncPolling && (
                                                <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse" title="Checking status every 30 seconds"></div>
                                            )}
                                        </div>
                                    )}
                                    <div className="mt-1">
                                        <span className="font-medium">Status:</span>{' '}
                                        <span className={`font-semibold ${
                                            syncStatus === 'succeeded' ? 'text-green-600' :
                                            syncStatus === 'failed' ? 'text-red-600' :
                                            syncStatus === 'running' || syncStatus === 'pending' ? 'text-blue-600' :
                                            'text-gray-600'
                                        }`}>
                                            {syncStatus === 'succeeded' ? 'Completed' :
                                             syncStatus === 'failed' ? 'Failed' :
                                             syncStatus === 'running' || syncStatus === 'pending' ? 'In Progress' :
                                             'Pending'}
                                        </span>
                                    </div>
                                    {syncStatus === 'failed' && (
                                        <div className="mt-1">
                                            <span className="font-medium">Status:</span> <span className="text-red-600 font-semibold">Failed</span>
                                        </div>
                                    )}
                                </div>
                                <div className="mt-6">
                                    <p className="text-sm text-blue-800 mb-3">
                                        Notification will be sent to: <p className="font-semibold">{`${getUser()?.email}`}</p>
                                    </p>

                                    {!emailManager.state.showInput ? (
                                        <button
                                            type="button"
                                            onClick={emailManager.toggleInput}
                                            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 bg-white border border-blue-600 rounded-md hover:bg-blue-50 transition-colors"
                                        >
                                            <UserPlus className="w-4 h-4" />
                                            Add Additional Emails
                                        </button>
                                    ) : (
                                        <div className="space-y-2">
                                            <label className="block text-sm text-blue-900">
                                                Add additional email addresses where you’d like to receive notifications.
                                            </label>



                                            {/* Email input */}
                                            <div className="relative">
                                                <input
                                                    type="text"
                                                    value={emailManager.state.inputValue}
                                                    onChange={(e) => emailManager.handleInputChange(e.target.value)}
                                                    onKeyDown={emailManager.handleKeyDown}
                                                    placeholder="Enter email and press Enter or comma"
                                                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm ${emailManager.state.error ? 'border-red-500' : 'border-gray-300'
                                                        }`}
                                                />
                                                {emailManager.state.error && (
                                                    <p className="text-red-600 text-xs mt-1">{emailManager.state.error}</p>
                                                )}
                                            </div>
                                            {/* Email chips display */}
                                            {emailManager.state.emails.length > 0 && (
                                                <div className="flex flex-wrap gap-2 mb-2">
                                                    {emailManager.state.emails.map((email, index) => (
                                                        <div
                                                            key={index}
                                                            className="flex items-center gap-1 px-3 py-1 bg-gray-200 rounded-full text-sm"
                                                        >
                                                            <span>{email}</span>
                                                            <button
                                                                type="button"
                                                                onClick={() => emailManager.removeEmail(email)}
                                                                className="hover:bg-blue-200 rounded-full p-0.5 transition-colors"
                                                            >
                                                                <X className="w-3 h-3" />
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            {/* Update button */}
                                            {emailManager.state.showUpdateButton && (
                                                <button
                                                    type="button"
                                                    onClick={handleUpdateNotificationEmails}
                                                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium transition-colors"
                                                >
                                                    Update Notification Emails
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {showResyncAction && (
                    <>
                        <p>
                            {effectiveFlowMode === 'syncDateOnly'
                                ? 'Start a new sync with modified configurations.'
                                : (effectiveFlowMode === 'apiKeyOnly' || effectiveFlowMode === 'both')
                                    ? 'Start a new sync with modified configurations.'
                                    : 'Start ingestion of your data from Stripe by starting the data sync. First time syncing of all data will take a while to populate the revenue analytics dashboard.'}
                        </p>

                        <button
                            type="button"
                            onClick={handleStartIngestion}
                            disabled={
                                (effectiveFlowMode === 'apiKeyOnly')
                                    ? (!step3Completed || triggerSyncLoading || updateAirbyteSourceLoading)
                                    : (effectiveFlowMode === 'syncDateOnly' || effectiveFlowMode === 'both')
                                        ? (!step3Completed || resetAndResyncLoading || updateAirbyteSourceLoading)
                                        : (!testCompleted || createIntegrationLoading)
                            }
                            className="px-6 py-3 rounded-md bg-blue-600 hover:bg-blue-700 text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                        >
                            {(createIntegrationLoading || triggerSyncLoading || resetAndResyncLoading) && <Loader2 className="w-4 h-4 animate-spin" />}
                            <span>
                                {(effectiveFlowMode === 'apiKeyOnly' || effectiveFlowMode === 'both')
                                    ? (triggerSyncLoading ? 'Starting Resync...' : 'Resync Stripe data')
                                    : (effectiveFlowMode === 'syncDateOnly')
                                        ? (resetAndResyncLoading ? 'Starting Resync...' : 'Resync Stripe data')
                                        : (createIntegrationLoading ? 'Starting Ingestion...' : 'Start Ingestion')}
                            </span>
                        </button>

                        {isStep3Incomplete && <p><strong>Note:</strong> You can start Data Ingestion now and see revenue analytics data and fix the schema for data correlation later. </p>}
                    </>
                )}
                {/* Error Display */}
                {ingestionError && (
                    <div className="bg-red-50 rounded-lg p-4">
                        <div className="flex items-start space-x-3">
                            <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                            <div className="flex-1">
                                <h4 className="text-sm font-semibold text-red-900 mb-1">
                                    Failed to Start Ingestion
                                </h4>
                                <p className="text-sm text-red-800">Something went wrong, Please try again.</p>
                            </div>
                        </div>
                    </div>
                )}
                {!isResyncFlow && <div>{renderIngestionLoadingSteps()}</div>}
                {showStatusPanel && (
                    <div className="mt-4 ml-6 space-y-4">
                        <div>You can continue to <strong>Core Revenue Calculation</strong> to set up currency and other fee-related configurations while the sync completes in background.</div>
                    </div>
                )}
            </div>
        );
    };

    const renderStepContent = (step: StepConfig) => {
        switch (step.id) {
            case 'environment':
                return renderEnvironmentContent(step);
            case 'basic-config':
                return renderBasicConfigContent();
            case 'revenue-correlation':
                return renderRevenueCorrelationContent();
            case 'pause-fix':
                return renderPauseFixContent();
            default:
                return null;
        }
    };

    return (
        <div className="bg-white">
            <div className="container mt-8">
                <div className="">
                    <div className="mx-auto w-full bg-white rounded-lg">
                        <form onSubmit={onSubmit} className="space-y-8">
                            {/* Steps Container */}
                            <div className="relative">
                                {/* Connector line */}
                                <div
                                    className="absolute left-5 w-0.5 bg-gray-300"
                                    style={getConnectorLineStyle()}
                                />

                                <div className="bg-white rounded-lg shadow-sm border border-gray-200 relative z-10 ml-10">
                                    {stepsConfig.map((step, idx) => (
                                        <div
                                            key={step.id}
                                            ref={step.id === 'basic-config' ? step2ContainerRef : step.id === 'revenue-correlation' ? step3ContainerRef : step.id === 'pause-fix' ? step4ContainerRef : null}
                                            className={`border-b border-gray-200 last:border-b-0 transition-all duration-300 ease-in-out ${step.isActive ? 'bg-blue-50' : ''
                                                }`}
                                        >
                                            {(() => {
                                                const step3IsCompleteForGating = step3Completed;
                                                const hasUnacknowledgedPendingChanges = (pendingSecretKeyChange || pendingSyncDateChange) && 
                                                    !isBasicConfigStepCompleted;
                                                const baseLocked =
                                                    (step.id === 'revenue-correlation' || step.id === 'pause-fix') &&
                                                    (!isBasicConfigStepCompleted || hasUnacknowledgedPendingChanges);
                                                const lockedByStep3 =
                                                    requiresStep3CompletionForResync &&
                                                    step.id === 'pause-fix' &&
                                                    !step3IsCompleteForGating;
                                                const isLockedStep = baseLocked || lockedByStep3;
                                                return (
                                                    <div className={`relative ${isLockedStep ? 'group' : ''}`}>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    if (isLockedStep) return;
                                                    setIsManualStepChange(true);
                                                    const nextStep = step.isActive ? '' : step.id;
                                                    if (nextStep) userChosenStepRef.current = nextStep;
                                                    setActiveStep(nextStep);
                                                }}
                                                aria-disabled={isLockedStep}
                                                className={`w-full flex items-center justify-between p-4 text-left transition-colors ${
                                                    isLockedStep ? 'cursor-not-allowed opacity-70' : 'hover:bg-gray-50'
                                                }`}
                                            >
                                                <div className="flex items-center gap-4 flex-1">
                                                    {/* Step numbers */}
                                                    <div
                                                        ref={step.id === 'environment' ? step1NumberRef : step.id === 'basic-config' ? step2NumberRef : step.id === 'revenue-correlation' ? step3NumberRef : step4NumberRef}
                                                        className={`absolute -left-10 w-8 h-8 flex items-center justify-center rounded-full font-semibold text-sm z-10 border-2 transition-all duration-300 ${step.isCompleted
                                                            ? 'border-green-500'
                                                            : step.isActive
                                                                ? 'bg-blue-500 text-white border-blue-500'
                                                                : 'bg-gray-100 text-gray-600 border-gray-300'
                                                            }`}
                                                        style={step.isCompleted ? {
                                                            backgroundColor: 'rgb(34, 197, 94)',
                                                            color: 'rgb(255, 255, 255)'
                                                        } : {}}
                                                    >
                                                        {step.number}
                                                    </div>

                                                    <div className="flex-1">
                                                        <div className="flex items-center justify-between gap-4">
                                                            {/* Left: Title and Required Indicator */}
                                                            <div className="flex items-center gap-2">
                                                                <div className="font-semibold text-gray-900 text-lg">{step.title}</div>
                                                                {step.required && !isStep3Incomplete && (
                                                                    <span className="text-red-800 text-lg">*</span>
                                                                )}
                                                            </div>

                                                            {/* Environment connection diagram */}
                                                            {step.id === 'environment' && step.isCompleted && (
                                                                <div className="flex items-center">{renderEnvironmentDiagram(testCompleted)}</div>
                                                            )}

                                                            {/* Middle + Right: Diagram and Status Badges grouped together */}
                                                            <div className="flex items-center gap-4">
                                                                {/* Status badges */}
                                                                {step.isCompleted && (
                                                                    <div className="px-3 py-1 rounded-full text-xs font-medium bg-green-50 text-green-800">
                                                                        Completed
                                                                    </div>
                                                                )}

                                                                {step.id === 'revenue-correlation' && isStep3Incomplete && (
                                                                    <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-500 text-red-100 h-5">
                                                                        <AlertTriangle className="w-3 h-3" />
                                                                        <span>Action Required</span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                        {
                                                            step.id === 'environment' ?
                                                                <div className="text-sm text-gray-500">{step.subtitle}</div> :
                                                                <div className="text-sm text-gray-500">{step.description}</div>
                                                        }
                                                    </div>
                                                </div>

                                                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-300 ml-2 ${step.isActive ? 'rotate-180' : ''
                                                    }`} />
                                            </button>
                                                        {isLockedStep && (
                                                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-20">
                                                                {lockedByStep3 ? 'Complete Step 3 to proceed.' : 'Complete Basic Configurations Step.'}
                                                                <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })()}

                                            {step.isActive && (
                                                <div className="border-t border-gray-200 p-6 bg-white transition-all duration-300 ease-in-out">
                                                    {renderStepContent(step)}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            {/* Setup Guide Modal */}
            {isSetupGuideOpen && (
                <div className="fixed inset-0 z-50 overflow-hidden">
                    <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setIsSetupGuideOpen(false)} />
                    <div className="absolute right-0 top-0 h-full w-[600px] bg-white shadow-xl overflow-y-auto">
                        <div className="p-4 border-b border-gray-200">
                            <div className="flex items-center justify-between">
                                <h2 className="text-xl font-semibold">Setup Guide: Stripe</h2>
                                <button
                                    onClick={() => setIsSetupGuideOpen(false)}
                                    className="text-gray-400 hover:text-gray-600 transition-colors"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                        </div>
                        <div className="p-6">
                            <SetupGuide step3Ref={setupGuideStep3Ref} accountIdRef={setupGuideAccountIdRef} setExpanded={setExpanded} expanded={expanded} />
                        </div>
                    </div>
                </div>
            )}

            <GroupIdMappingModal
                open={showMappingModal}
                onOpenChange={setShowMappingModal}
                onProceed={handleProceedAnyway}
            />

            {showInviteModal && (
                <InviteTeamModal
                    isOpen={showInviteModal}
                    onClose={() => setShowInviteModal(false)}
                    team={selectedTeam}
                    onInvite={() => { }}
                    source={selectedSources === 'stripe' ? selectedSources : undefined}
                />
            )}

            {/* Fix Instructions Modal */}
            <FixInstructionsModal
                isOpen={showFixInstructionsModal}
                onClose={() => setShowFixInstructionsModal(false)}
                onInviteTeam={handleInviteTeam}
                accountIdentifierKey={DEFAULT_ACCOUNT_IDENTIFIER_KEY}
            />


            {/* Environment Modal */}
            {showNewEnvironmentForm && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-20">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
                        <div className="flex items-center justify-between p-6 border-b border-gray-200">
                            <h3 className="text-lg font-semibold text-gray-900">Create New Environment</h3>
                            <button
                                onClick={handleCancelNewEnvironment}
                                className="text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Environment Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={newEnvironmentForm.displayName}
                                    onChange={(e) => setNewEnvironmentForm(prev => ({ ...prev, displayName: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="Enter environment name"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Environment Type <span className="text-red-500">*</span>
                                </label>
                                <select
                                    value={newEnvironmentForm.environmentType}
                                    onChange={(e) => setNewEnvironmentForm(prev => ({ ...prev, environmentType: e.target.value as EnvironmentType }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                    <option value={EnvironmentType.Production}>Production</option>
                                    <option value={EnvironmentType.NonProduction}>NonProduction</option>
                                </select>
                            </div>
                        </div>

                        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
                            <button
                                onClick={handleCancelNewEnvironment}
                                className="px-4 py-2 text-sm font-medium text-blue-600 bg-white border border-blue-600 rounded-md hover:bg-blue-50 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleCreateNewEnvironment}
                                disabled={!newEnvironmentForm.displayName || createEnvironmentLoading}
                                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
                            >
                                {createEnvironmentLoading && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>}
                                <span>{createEnvironmentLoading ? 'Creating...' : 'Create Environment'}</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
